import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BookOpen,
  RefreshCw,
  Mail,
  Clock,
  CheckCircle2,
  Circle,
  Eye,
  ArrowRight,
  AlertTriangle,
  Building2,
  User,
  ShieldCheck,
  Sparkles,
  Send,
  XCircle,
  MessageSquare,
  Upload,
  FileSpreadsheet,
  FileText,
  Ban,
  Paperclip,
  Link2,
  Braces,
  Workflow,
  GitBranch,
  FolderOpen,
  Info,
  ClipboardCheck,
} from 'lucide-react';
import { ANTARCTICA_DEALS as DEMO_ANTARCTICA_DEALS } from '@/data/clientMockData';
import { useDemoDataSettings } from '@/context/DemoDataContext';
import IntakeAnalyticsStrip from '@/components/intake/IntakeAnalyticsStrip';
import DataReviewInbox from '@/components/review/DataReviewInbox';
import { describeDesk } from '@/components/review/dataReviewFormat';
import ReparseNotice from '@/components/review/ReparseNotice';
import {
  ingestionService,
  type Ingestion,
  type ParseReport,
  type ParseConflict,
  type ParseProgressSnapshot,
  type SourceAnchor,
  type ScreenResults,
  type EmailEnvelope,
  type AttachmentReport,
  type ThreadInfo,
  type ReingestUpdates,
  type BaselineDiffEntry,
  type SourceFileRef,
  type GroupingMode,
  type MatchCandidate,
  type IntakeAnalytic,
  type InboundMailStatus,
  type PendingRevision,
  type HoldState,
  type ConvertPayload,
  type UploadResult,
} from '@/services/ingestion';
import { duplicateUploadNotice, queueCardSummary, recordDisposition, threadSeparatedAs } from '@/components/intake/recordDisposition';
import { fmtThreadValue, reingestBasisLabel, supersededNotes } from '@/components/intake/threadFormat';
import EmailedIntakeTable, { ORIGINATION_MAILBOX } from '@/components/intake/EmailedIntakeTable';
import IngestedCommunicationPanel, { buildDemoEmailBody } from '@/components/intake/IngestedCommunicationPanel';
import ManualDealEntry from '@/components/intake/ManualDealEntry';
import SourceViewerDialog from '@/components/intake/SourceViewerDialog';
import SourcePreviewPane from '@/components/intake/SourcePreviewPane';
import DataReviewPanel from '@/components/review/DataReviewPanel';
import SmartReviewSummaryCard, {
  smartReviewSummaryCounts,
} from '@/components/intake/SmartReviewSummaryCard';
import MatchPreviewDialog from '@/components/intake/MatchPreviewDialog';
import HoldReasonNotRecorded from '@/components/intake/HoldReasonNotRecorded';
import ConfirmNewDealDialog from '@/components/intake/ConfirmNewDealDialog';
import { ConfirmationViewDialog } from '@/components/intake/ConfirmationView';
import { ManagerQueueButton } from '@/components/intake/ConfirmationViewQueueButton';
import {
  confirmationFromRefusal,
  newDealConfirmation,
  type NewDealConfirmation,
} from '@/components/intake/newDealConfirmation';
import { requiredGapsFromRefusal } from '@/components/intake/requiredAtConfirm';
import InfoRequestDialog from '@/components/intake/InfoRequestDialog';
import RerunDialog from '@/components/intake/RerunDialog';
import DossierCoveragePanel from '@/components/intake/DossierCoveragePanel';
import ArchiveOutcome, { ArchiveOutcomeList } from '@/components/intake/ArchiveOutcome';
import AttachmentStatusBadge from '@/components/intake/AttachmentStatusBadge';
import { RequiredFieldsPanel } from '@/components/intake/RequiredFieldsPanel';
import BrokerResolutionCard from '@/components/intake/BrokerResolutionCard';
import DealFileDialog from '@/components/deal/DealFileDialog';
import { checkBasis, checkDetail } from '@/components/deal/dossier/screeningBasis';
import { inboxQuery, useDataReviewInboxView } from '@/store/dataReviewInboxStore';
import { useDealDossierStore } from '@/store/dealDossierStore';
import { formatUsd } from '@/utils/money';
import { formatDeskDateTime } from '@/utils/dates';


// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type RequestStatus = 'new' | 'reviewed' | 'converted' | 'declined';
type MandateFit = 'In Box' | 'Stretch' | 'Out';
type Priority = 'High' | 'Medium' | 'Low';
type IntakeTab = 'queue' | 'emailed' | 'review' | 'upload' | 'manual';
export const INTAKE_TABS: ReadonlyArray<readonly [IntakeTab, string]> = [
  ['queue', 'Ingestion Queue'],
  ['emailed', 'Emailed Intake'],
  ['review', 'Data Intelligence Review'],
  ['upload', 'Upload / Parse Files'],
  ['manual', 'Manual Entry'],
];

interface ExtractedData {
  property: string;
  type: string;
  unitsSf: string;
  location: string;
  amountRequested: string;
  sponsor: string;
  purpose: string;
  occupancy: string;
  askingCapRate: string;
  /** Descriptive prose split off the "Sponsor:" line (register A4/A5). Live records only. */
  sponsorNarrative?: string | null;
}

interface AiAssessment {
  mandateFit: MandateFit | null;   // null on live records — the credit-box screen is authoritative
  suggestedProduct: string;
  estMaxLtv: string;
  estDscr: string;
  priority: Priority;
  riskFlags: string[];
}

interface Attachment {
  filename: string;
  type: string;         // email | excel | pdf | word
  sizeKb: number;
  parsed: boolean;
  traceFields?: string[];   // fields traced back to source
  /** Present on live records when the binary is addressable by content hash. */
  sha256?: string;
}

/** Resolve an attachment metadata row to a stored source file when possible. */
function resolveAttachmentSource(
  att: { filename: string; sha256?: string },
  refs: SourceFileRef[] | undefined,
): SourceFileRef | null {
  const list = refs ?? [];
  const lower = att.filename.toLowerCase();
  const byName =
    list.find((f) => f.filename === att.filename) ??
    list.find((f) => f.filename.toLowerCase() === lower) ??
    list.find(
      (f) =>
        f.filename.toLowerCase().includes(lower) ||
        lower.includes(f.filename.toLowerCase()),
    );
  if (byName) return byName;
  if (att.sha256) {
    return { filename: att.filename, kind: 'attachment', sha256: att.sha256 };
  }
  return null;
}

export interface IntakeRequest {
  id: string;
  status: RequestStatus;
  receivedAt: string;
  relativeTime: string;
  from: string;
  subject: string;
  broker: string;
  brokerFirm: string;
  brokerId?: string;
  brokerFirmId?: string;
  propertyType: string;
  briefDescription: string;
  amount: string;
  sourceType: 'email' | 'excel' | 'pdf' | 'manual';
  attachments?: Attachment[];
  extracted: ExtractedData;
  aiAssessment: AiAssessment;
  createdDealId?: string;
  // Traceability (Item 3)
  fieldMapping?: Record<string, { sourceDock: string; cellRef?: string }>;
  outboundRequiresHuman: boolean;   // Item 4: always true
  // Deterministic engine evidence (live records only)
  parseReport?: ParseReport | null;
  /** Off the pipeline with no hold reason saved (served beside the record). */
  holdState?: HoldState | null;
  screenResults?: ScreenResults | null;
  emailEnvelope?: EmailEnvelope | null;
  rawFilePaths?: SourceFileRef[];
}

// ---------------------------------------------------------------------------
// Mock data
// ---------------------------------------------------------------------------

const INTAKE_REQUESTS: IntakeRequest[] = [
  {
    id: 'INQ-001',
    status: 'new',
    receivedAt: '2026-03-19 10:42 AM',
    relativeTime: '2 hours ago',
    from: 'mike.harris@cbre.com',
    subject: 'Financing Request — Desert Ridge Apartments, Phoenix',
    broker: 'Mike Harris',
    brokerFirm: 'CBRE',
    propertyType: 'Multifamily',
    briefDescription: '280-unit garden-style Phoenix',
    amount: '$42M',
    sourceType: 'email',
    attachments: [
      { filename: 'DesertRidge_OM.pdf', type: 'pdf', sizeKb: 4200, parsed: true, traceFields: ['occupancy', 'cap_rate', 'noi'] },
      { filename: 'T12_DesertRidge_2025.xlsx', type: 'excel', sizeKb: 890, parsed: true, traceFields: ['egr', 'expenses', 'noi', 'dscr'] },
    ],
    extracted: {
      property: 'Desert Ridge Apartments',
      type: 'Multifamily',
      unitsSf: '280 units',
      location: 'Phoenix, AZ',
      amountRequested: '$42,000,000',
      sponsor: 'Greenfield Real Estate Partners',
      purpose: 'Acquisition',
      occupancy: '94%',
      askingCapRate: '5.75%',
    },
    aiAssessment: {
      mandateFit: 'In Box',
      suggestedProduct: 'Senior Mortgage',
      estMaxLtv: '72%',
      estDscr: '1.38x',
      priority: 'High',
      riskFlags: [],
    },
    fieldMapping: {
      'EGR': { sourceDock: 'T12_DesertRidge_2025.xlsx', cellRef: 'C14' },
      'NOI': { sourceDock: 'T12_DesertRidge_2025.xlsx', cellRef: 'C28' },
      'Occupancy': { sourceDock: 'DesertRidge_OM.pdf', cellRef: undefined },
    },
    outboundRequiresHuman: true,
  },
  {
    id: 'INQ-002',
    status: 'new',
    receivedAt: '2026-03-19 07:18 AM',
    relativeTime: '5 hours ago',
    from: 'sarah.williams@jll.com',
    subject: 'Refinance Inquiry — Meridian Office Tower, Denver',
    broker: 'Sarah Williams',
    brokerFirm: 'JLL',
    propertyType: 'Office',
    briefDescription: '150K SF Class A Denver CBD',
    amount: '$28M',
    sourceType: 'excel',
    attachments: [
      { filename: 'Meridian_UW_NonStandard.xlsx', type: 'excel', sizeKb: 1100, parsed: true, traceFields: ['ltv', 'dscr', 'loan_amount'] },
    ],
    extracted: {
      property: 'Meridian Office Tower',
      type: 'Office',
      unitsSf: '150,000 SF',
      location: 'Denver, CO',
      amountRequested: '$28,000,000',
      sponsor: 'Pikes Peak Capital',
      purpose: 'Refinance',
      occupancy: '87%',
      askingCapRate: '7.25%',
    },
    aiAssessment: {
      mandateFit: 'Stretch',
      suggestedProduct: 'Senior Mortgage',
      estMaxLtv: '62%',
      estDscr: '1.52x',
      priority: 'Medium',
      riskFlags: ['Elevated office vacancy in submarket', 'Near-term lease rollover 22%'],
    },
    outboundRequiresHuman: true,
  },
  {
    id: 'INQ-003',
    status: 'new',
    receivedAt: '2026-03-19 06:45 AM',
    relativeTime: '6 hours ago',
    from: 'jason.lee@nmrk.com',
    subject: 'Senior Living Acquisition — Sunset Gardens, Scottsdale',
    broker: 'Jason Lee',
    brokerFirm: 'Newmark',
    propertyType: 'Senior Housing',
    briefDescription: '120-bed assisted living Scottsdale',
    amount: '$31M',
    sourceType: 'email',
    attachments: [
      { filename: 'SunsetGardens_OM.pdf', type: 'pdf', sizeKb: 3400, parsed: true, traceFields: ['occupancy', 'beds', 'noi'] },
      { filename: 'RentRoll_SunsetGardens_Q4.xlsx', type: 'excel', sizeKb: 680, parsed: true, traceFields: ['unit_count', 'avg_rent', 'occupancy'] },
    ],
    extracted: {
      property: 'Sunset Gardens Senior Living',
      type: 'Senior Housing',
      unitsSf: '120 beds',
      location: 'Scottsdale, AZ',
      amountRequested: '$31,000,000',
      sponsor: 'Evergreen Healthcare REIT',
      purpose: 'Acquisition',
      occupancy: '91%',
      askingCapRate: '6.50%',
    },
    aiAssessment: {
      mandateFit: 'In Box',
      suggestedProduct: 'Senior Mortgage',
      estMaxLtv: '68%',
      estDscr: '1.42x',
      priority: 'High',
      riskFlags: [],
    },
    outboundRequiresHuman: true,
  },
  {
    id: 'INQ-004',
    status: 'reviewed',
    receivedAt: '2026-03-18 02:30 PM',
    relativeTime: '1 day ago',
    from: 'david.chen@cushwake.com',
    subject: 'Industrial Portfolio — Southwest Logistics Hub',
    broker: 'David Chen',
    brokerFirm: 'Cushman & Wakefield',
    propertyType: 'Industrial',
    briefDescription: '200K SF warehouse Dallas',
    amount: '$35M',
    sourceType: 'email',
    attachments: [
      { filename: 'SWLogistics_Deck.pdf', type: 'pdf', sizeKb: 2100, parsed: true, traceFields: ['noi', 'occupancy', 'cap_rate'] },
    ],
    extracted: {
      property: 'Southwest Logistics Hub',
      type: 'Industrial',
      unitsSf: '200,000 SF',
      location: 'Dallas, TX',
      amountRequested: '$35,000,000',
      sponsor: 'Lone Star Distribution LLC',
      purpose: 'Acquisition',
      occupancy: '97%',
      askingCapRate: '5.50%',
    },
    aiAssessment: {
      mandateFit: 'In Box',
      suggestedProduct: 'Large Loan',
      estMaxLtv: '70%',
      estDscr: '1.48x',
      priority: 'High',
      riskFlags: [],
    },
    outboundRequiresHuman: true,
  },
  {
    id: 'INQ-005',
    status: 'reviewed',
    receivedAt: '2026-03-18 09:15 AM',
    relativeTime: '1 day ago',
    from: 'amanda.ross@marcusmillichap.com',
    subject: 'Mixed-Use Refinance — The Lexington, Austin',
    broker: 'Amanda Ross',
    brokerFirm: 'Marcus & Millichap',
    propertyType: 'Mixed-Use',
    briefDescription: '85K SF retail/office Austin',
    amount: '$18M',
    sourceType: 'email',
    extracted: {
      property: 'The Lexington',
      type: 'Mixed-Use',
      unitsSf: '85,000 SF',
      location: 'Austin, TX',
      amountRequested: '$18,000,000',
      sponsor: 'Hill Country Ventures',
      purpose: 'Refinance',
      occupancy: '82%',
      askingCapRate: '6.80%',
    },
    aiAssessment: {
      mandateFit: 'Stretch',
      suggestedProduct: 'Senior Mortgage',
      estMaxLtv: '60%',
      estDscr: '1.31x',
      priority: 'Low',
      riskFlags: ['Below-target occupancy', 'Mixed-use complexity', 'Retail component softening'],
    },
    outboundRequiresHuman: true,
  },
  {
    id: 'INQ-006',
    status: 'converted',
    receivedAt: '2026-03-17 11:00 AM',
    relativeTime: '2 days ago',
    from: 'peter.grant@eastdil.com',
    subject: 'Hospitality Acquisition — Grand Mesa Resort',
    broker: 'Peter Grant',
    brokerFirm: 'Eastdil Secured',
    propertyType: 'Hospitality',
    briefDescription: '220-key full-service Sedona',
    amount: '$52M',
    sourceType: 'email',
    extracted: {
      property: 'Grand Mesa Resort & Spa',
      type: 'Hospitality',
      unitsSf: '220 keys',
      location: 'Sedona, AZ',
      amountRequested: '$52,000,000',
      sponsor: 'Red Rock Hospitality Group',
      purpose: 'Acquisition',
      occupancy: '78%',
      askingCapRate: '7.00%',
    },
    aiAssessment: {
      mandateFit: 'In Box',
      suggestedProduct: 'Senior Mortgage',
      estMaxLtv: '65%',
      estDscr: '1.55x',
      priority: 'High',
      riskFlags: [],
    },
    createdDealId: 'ALG-1001',
    outboundRequiresHuman: true,
  },
  {
    id: 'INQ-007',
    status: 'converted',
    receivedAt: '2026-03-16 03:20 PM',
    relativeTime: '3 days ago',
    from: 'lisa.kim@cbre.com',
    subject: 'Multifamily Bridge — Paloma at Kierland',
    broker: 'Lisa Kim',
    brokerFirm: 'CBRE',
    propertyType: 'Multifamily',
    briefDescription: '310-unit value-add Scottsdale',
    amount: '$47M',
    sourceType: 'email',
    extracted: {
      property: 'Paloma at Kierland',
      type: 'Multifamily',
      unitsSf: '310 units',
      location: 'Scottsdale, AZ',
      amountRequested: '$47,000,000',
      sponsor: 'Sonoran Residential Partners',
      purpose: 'Acquisition — Value-Add',
      occupancy: '88%',
      askingCapRate: '5.40%',
    },
    aiAssessment: {
      mandateFit: 'In Box',
      suggestedProduct: 'Senior Mortgage',
      estMaxLtv: '72%',
      estDscr: '1.35x',
      priority: 'High',
      riskFlags: [],
    },
    createdDealId: 'ALG-1004',
    outboundRequiresHuman: true,
  },
  {
    id: 'INQ-008',
    status: 'declined',
    receivedAt: '2026-03-15 10:00 AM',
    relativeTime: '4 days ago',
    from: 'tom.baker@nmrk.com',
    subject: 'Land Acquisition — Desert Bloom Parcel',
    broker: 'Tom Baker',
    brokerFirm: 'Newmark',
    propertyType: 'Land',
    briefDescription: 'Raw land 40 acres Maricopa',
    amount: '$8M',
    sourceType: 'email',
    extracted: {
      property: 'Desert Bloom Parcel',
      type: 'Land',
      unitsSf: '40 acres',
      location: 'Maricopa, AZ',
      amountRequested: '$8,000,000',
      sponsor: 'Southwest Land Holdings',
      purpose: 'Speculative Acquisition',
      occupancy: 'N/A',
      askingCapRate: 'N/A',
    },
    aiAssessment: {
      mandateFit: 'Out',
      suggestedProduct: 'N/A',
      estMaxLtv: 'N/A',
      estDscr: 'N/A',
      priority: 'Low',
      riskFlags: ['Raw land — outside lending mandate', 'No income stream', 'Speculative'],
    },
    outboundRequiresHuman: true,
  },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** The demo deal list this page shows alongside intake.
 *
 *  This used to return four headline METRICS as well — new-today, average
 *  response hours, conversion rate, deals-this-week — every one of which
 *  evaluated to a literal 0 (or a hard-coded 4.2) whenever `demo` was false.
 *  They were rendered in the page's top strip on live accounts, so an operator
 *  read invented figures about their own desk. `IntakeAnalyticsStrip` now shows
 *  measured intake figures there instead, and shows nothing at all in demo. */
function intakeDemoDeals(demo: boolean) {
  return demo
    ? DEMO_ANTARCTICA_DEALS.slice(0, 5).map((d) => ({
        property: d.property,
        amount: d.loanAmount,
        stage: d.stage,
      }))
    : [];
}

const statusDot = (s: RequestStatus) => {
  if (s === 'new') return <Circle size={8} className="text-blue-400 fill-blue-400" />;
  if (s === 'reviewed') return <Eye size={10} className="text-amber-400" />;
  if (s === 'converted') return <CheckCircle2 size={10} className="text-emerald-400" />;
  return <XCircle size={10} className="text-red-400" />;
};

const sourceIcon = (type: string) => {
  if (type === 'excel') return <FileSpreadsheet size={10} className="text-emerald-400" />;
  if (type === 'pdf') return <FileText size={10} className="text-red-400" />;
  return <Mail size={10} className="text-blue-400" />;
};

const stageBadge = (stage: string) => {
  const cls =
    stage === 'Triage' ? 'text-blue-400 bg-blue-400/10 border-blue-400/20' :
    stage === 'Funded' ? 'text-emerald-400 bg-emerald-400/10 border-emerald-400/20' :
    'text-amber-400 bg-amber-400/10 border-amber-400/20';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium border ${cls}`}>
      {stage}
    </span>
  );
};

// ---------------------------------------------------------------------------
// Live-data helpers (used when demo mode is off — real ingestion records)
// ---------------------------------------------------------------------------

function pctOrDash(v?: number): string {
  if (v == null) return '—';
  return `${(v <= 1 ? v * 100 : v).toFixed(1)}%`;
}

function fmtAmountM(amt?: number): string {
  if (amt == null) return '—';
  // Extraction stores DOLLARS ("$42M" is 42,000,000). The unit is never
  // guessed from the size: "under 1,000 is already $MM" printed $850 as $850.0M.
  return formatUsd(amt);
}

function relTime(iso?: string): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const mins = Math.max(0, Math.round((Date.now() - then) / 60000));
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs > 1 ? 's' : ''} ago`;
  return `${Math.round(hrs / 24)} day(s) ago`;
}

/**
 * Which record the detail pane shows — and, more to the point, which it must
 * REFUSE to show.
 *
 * The list backing this pane is the ingestion queue, which returns only `new`
 * and `reviewed`. Emailed Intake lists every email, converted ones included, and
 * global search deep-links by id. Both could therefore name a record the list
 * cannot hold, and the previous rule ended `|| visibleRequests[0]` — so asking
 * for one deal silently opened another. Showing nothing is recoverable; showing
 * a different deal's figures under the heading the operator asked for is not.
 *
 * `hydrated` is that record fetched by id. The first row stands in only when
 * nothing was asked for at all, which is the initial-load case.
 */
export function resolveSelectedRequest(
  visible: IntakeRequest[],
  selectedId: string,
  hydrated: IntakeRequest | undefined,
): IntakeRequest | undefined {
  const inList = visible.find((r) => r.id === selectedId);
  if (inList) return inList;
  if (selectedId) return hydrated?.id === selectedId ? hydrated : undefined;
  return visible[0];
}

/**
 * The asset class a request card shows: the one its credit box screened on,
 * which is the thread-reconciled answer Required Fields shows (INT-22). The
 * card read the message's own column, so The Marlowe's draft terms (which
 * state no class) read "—" beside a detail pane showing Multifamily. Falls
 * back to the message's own reading when no screen ran.
 */
export function intakeAssetType(i: Pick<Ingestion, 'extractedAssetType' | 'screenResults'>): string {
  const screened = i.screenResults?.runs?.[0]?.inputs.find((input) => input.field === 'asset_class')?.value
  if (typeof screened === 'string' && screened.trim()) {
    return screened
      .trim()
      .split(/[_\s]+/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
  }
  return i.extractedAssetType || '—'
}

/** Map a backend ingestion record into the rich IntakeRequest shape the UI renders. */
function mapIngestionToRequest(i: Ingestion): IntakeRequest {
  const amount = fmtAmountM(i.extractedLoanAmount);
  const sourceType: IntakeRequest['sourceType'] =
    i.sourceType === 'excel' ? 'excel' : i.sourceType === 'pdf' ? 'pdf' : i.sourceType === 'email' ? 'email' : 'manual';
  const brokerResolution = i.parseReport?.brokerResolution;
  const proposedBroker = brokerResolution?.candidates.contact.find((candidate) => candidate.id == null)?.name;
  const proposedFirm = brokerResolution?.candidates.firm.find((candidate) => candidate.id == null)?.name;
  return {
    id: i.id,
    status: (i.status === 'deferred' ? 'reviewed' : i.status) as RequestStatus,
    receivedAt: formatDeskDateTime(i.receivedAt),
    relativeTime: relTime(i.createdAt || i.receivedAt),
    from: i.sourceEmail ?? '—',
    subject: i.sourceSubject ?? i.attachments?.[0]?.filename ?? 'Uploaded deal package',
    broker: i.brokerName ?? proposedBroker ?? '—',
    brokerFirm: proposedFirm ?? 'Unresolved',
    brokerId: i.brokerId,
    brokerFirmId: i.brokerFirmId,
    propertyType: intakeAssetType(i),
    briefDescription: i.extractedPropertyName ?? i.sourceSubject ?? 'Ingested deal',
    amount,
    sourceType,
    attachments: (i.attachments ?? []).map((a) => ({
      filename: a.filename,
      type: a.fileType ?? '',
      sizeKb: a.sizeKb ?? 0,
      parsed: a.parsed ?? true,
      sha256: a.sha256,
    })),
    extracted: {
      property: i.extractedPropertyName ?? '—',
      type: i.extractedAssetType ?? '—',
      unitsSf: i.extractedUnitsOrSf ?? '—',
      location: i.extractedLocation ?? '—',
      amountRequested: amount,
      sponsor: i.extractedSponsor ?? '—',
      purpose: (typeof i.extractedFields?.purpose === 'string' ? i.extractedFields.purpose : null) ?? '—',
      occupancy: pctOrDash(i.extractedOccupancy),
      askingCapRate: pctOrDash(i.extractedCapRate),
      // Nested extracted_fields keys are camelCased by toCamelCase (sponsor_narrative → sponsorNarrative).
      sponsorNarrative:
        typeof i.extractedFields?.sponsorNarrative === 'string' ? i.extractedFields.sponsorNarrative : null,
    },
    aiAssessment: {
      mandateFit: (i.aiMandateFit as MandateFit) ?? null,
      suggestedProduct: i.aiSuggestedProduct ?? '—',
      estMaxLtv: pctOrDash(i.extractedLtv),
      estDscr: i.extractedDscr != null ? `${i.extractedDscr.toFixed(2)}x` : '—',
      priority: (i.aiPriority as Priority) ?? 'Medium',
      riskFlags: i.aiRiskFlags ?? [],
    },
    createdDealId: i.convertedToLoanId,
    fieldMapping: i.fieldMapping
      ? Object.fromEntries(
          Object.entries(i.fieldMapping).map(([k, v]) => [k, { sourceDock: v.sourceDoc ?? '', cellRef: v.cellRef }]),
        )
      : undefined,
    outboundRequiresHuman: true,
    parseReport: i.parseReport,
    holdState: i.holdState,
    screenResults: i.screenResults,
    emailEnvelope: i.emailEnvelope,
    rawFilePaths: i.rawFilePaths,
  };
}

// ---------------------------------------------------------------------------
// "What happens next" — makes the post-ingestion automation visible
// ---------------------------------------------------------------------------

function WhatHappensNext() {
  const steps = [
    { label: 'Ingest', desc: 'Email or file is parsed locally — no PII leaves your tenant.' },
    { label: 'Extract', desc: 'Deal fields are pulled out with source-cell traceability.' },
    { label: 'Review', desc: 'You confirm the extracted fields and mandate fit.' },
    { label: 'Convert', desc: 'One click creates the loan and links it to this ingestion.' },
    { label: 'Pipeline', desc: 'The deal lands at the “Received” stage, ready to work.' },
    { label: 'Downstream', desc: 'Underwriting, Committee, Closing & Surveillance all key off it.' },
  ];
  return (
    <div className="panel border-amber-400/15">
      <div className="panel-header">
        <div className="flex items-center gap-1.5">
          <Workflow size={11} className="text-amber-400" />
          <span className="panel-header-title">How ingestion is automated &amp; managed</span>
        </div>
      </div>
      <div className="panel-body grid grid-cols-2 gap-2 sm:grid-cols-3">
        {steps.map((s, i) => (
          <div key={s.label} className="rounded-lg border border-amber-400/10 bg-black/20 px-2.5 py-2">
            <div className="flex items-center gap-1.5">
              <span className="flex h-4 w-4 items-center justify-center rounded-full border border-amber-400/25 bg-amber-400/5 text-[9px] font-semibold text-amber-300">
                {i + 1}
              </span>
              <span className="text-[11px] font-medium text-stone-200">{s.label}</span>
            </div>
            <p className="mt-1 text-[10px] leading-snug text-stone-500">{s.desc}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Deterministic-engine evidence panels (shared by Upload result + Queue detail)
// ---------------------------------------------------------------------------

const screenOutcomeBadge = (outcome: string) => {
  const cls =
    outcome === 'PASS'
      ? 'text-emerald-400 bg-emerald-400/10 border-emerald-400/20'
      : outcome === 'EXCEPTION'
        ? 'text-amber-400 bg-amber-400/10 border-amber-400/20'
        : 'text-red-400 bg-red-400/10 border-red-400/20';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold border ${cls}`}>
      {outcome}
    </span>
  );
};

/** Build a click-to-source anchor from a conflict value's own provenance —
 *  the viewer locates by snippet, so this works for every source kind. */
function conflictAnchor(v: ParseConflict['values'][number]): SourceAnchor {
  const src = v.source ?? '';
  const kind: SourceAnchor['kind'] =
    src === 'email_subject' ? 'email_subject'
    : src.startsWith('email_body') ? 'email_body'
    : /\.pdf$/i.test(src) ? 'pdf'
    : /\.docx$/i.test(src) ? 'docx'
    : /\.(xlsx|xlsm)$/i.test(src) ? 'xlsx'
    : 'text';
  return { kind, lineNo: v.lineNo ?? null, snippet: v.raw ?? String(v.value ?? '') };
}

/** Market guidance stated by sale OMs / SOWs — shown as color, deliberately
 *  NOT merged into the deal's own structure fields (document-role policy). */
function GuidancePanel({ guidance }: { guidance: NonNullable<ParseReport['guidanceFields']> }) {
  if (!guidance.length) return null;
  return (
    <div className="panel border-sky-400/20">
      <div className="panel-header">
        <div className="flex items-center gap-1.5">
          <BookOpen size={11} className="text-sky-400" />
          <span className="panel-header-title text-sky-300">
            Market Guidance — from sale/marketing documents, not this deal's terms
          </span>
        </div>
        <span className="text-[10px] text-stone-600">{guidance.length}</span>
      </div>
      <div className="panel-body space-y-1">
        {guidance.map((g, i) => (
          <div key={i} className="flex items-center gap-2 text-[10px]">
            <span className="w-[120px] shrink-0 font-mono text-stone-400">{g.field}</span>
            <span className="font-mono text-sky-200">{String(g.value)}</span>
            <span className="text-stone-600">←</span>
            <span className="truncate text-stone-500" title={g.source}>{g.source}{g.lineNo ? ` · line ${g.lineNo}` : ''}</span>
          </div>
        ))}
        <p className="pt-1 text-[9px] text-stone-600">
          A guidance document speaks about market terms — these never merge into the deal's structure fields.
        </p>
      </div>
    </div>
  );
}

/** On-demand mapgen sweep for an unrecognized workbook. The minutes-long
 *  candidate-anchor scan runs here — at the operator's request during
 *  onboarding — never in the parse path. */
type SweepBlindSpots = {
  labelledValuesSeen: number
  recognised: number
  unrecognised: number
  coverage: number
  unrecognisedLabels: Array<{ label: string; count: number }>
}

function OnboardingSweepButton({ ingestionId, sha256 }: { ingestionId: string; sha256: string }) {
  const [state, setState] = useState<'idle' | 'running' | 'done' | 'error'>('idle');
  const [candidates, setCandidates] = useState<number | null>(null);
  const [blind, setBlind] = useState<SweepBlindSpots | null>(null);

  const run = async () => {
    setState('running');
    try {
      const r = await ingestionService.generateOnboardingDraft(ingestionId, sha256);
      setCandidates(r.candidates);
      setBlind(r.draft?.blindSpots ?? null);
      setState('done');
    } catch {
      setState('error');
    }
  };

  if (state === 'done') {
    return (
      <div className="mt-1 space-y-0.5">
        <p className="text-[9px] text-emerald-300">
          {candidates?.toLocaleString()} candidate anchors proposed — stored on the record for onboarding.
        </p>
        {/* The survey's own coverage. A candidate count alone says nothing
            about what was MISSED: an unrecognised label never becomes a
            candidate, so without this an empty field looks the same whether
            the document did not state it or the engine could not read it. */}
        {blind && (
          <p className="text-[9px] text-stone-500">
            read {blind.recognised.toLocaleString()} of{' '}
            {blind.labelledValuesSeen.toLocaleString()} labeled values (
            <span className={blind.coverage < 0.25 ? 'text-amber-400' : 'text-stone-400'}>
              {(blind.coverage * 100).toFixed(1)}%
            </span>
            ) — {blind.unrecognised.toLocaleString()} not recognized
            {blind.unrecognisedLabels.length > 0 && (
              <span
                className="ml-1 cursor-help underline decoration-dotted underline-offset-2"
                title={blind.unrecognisedLabels
                  .slice(0, 40)
                  .map((u) => `${u.count}x  ${u.label}`)
                  .join('\n')}
              >
                (most common: {blind.unrecognisedLabels.slice(0, 3).map((u) => u.label).join(', ')})
              </span>
            )}
          </p>
        )}
      </div>
    );
  }
  return (
    <div className="mt-1 flex items-center gap-2">
      <button
        type="button"
        onClick={run}
        disabled={state === 'running'}
        className="rounded border border-stone-700 bg-stone-800/60 px-2 py-0.5 text-[9px] text-stone-300 transition hover:border-amber-400/50 hover:text-amber-300 disabled:opacity-60"
      >
        {state === 'running' ? 'Scanning workbook… (the slow step — minutes on large models)' : 'Generate onboarding proposal'}
      </button>
      {state === 'error' && <span className="text-[9px] text-red-400">Scan failed — retry or download the workbook.</span>}
    </div>
  );
}

/** Determinate parse-progress bar: driven by the engine's REAL stage
 *  transitions (polled), with an ETA labeled as an estimate. Typical stages
 *  per file: read → attachments (i/N) → merge → screens → review. */
function ParseProgressBar({ jobId }: { jobId: string }) {
  const [snap, setSnap] = useState<ParseProgressSnapshot | null>(null);
  useEffect(() => {
    let live = true;
    const timer = setInterval(async () => {
      try {
        const s = await ingestionService.getParseProgress(jobId);
        if (live) setSnap(s);
      } catch {
        /* job not registered yet — keep polling */
      }
    }, 600);
    return () => {
      live = false;
      clearInterval(timer);
    };
  }, [jobId]);

  const stages = snap?.stages ?? [];
  const last = stages[stages.length - 1];
  // Expected stage count: ~5 engine stages per file, refined by any explicit
  // attachment i/N counters the engine has reported.
  const expected = Math.max(stages.length + 1, (snap?.totalFiles ?? 1) * 5);
  let pct = Math.min(95, Math.round((stages.length / expected) * 100));
  if (last?.index && last?.total) {
    pct = Math.max(pct, Math.min(95, Math.round((last.index / last.total) * 80)));
  }
  if (snap?.done) pct = 100;
  const elapsed = snap?.elapsedS ?? 0;
  const eta = pct > 4 && pct < 100 ? Math.max(1, Math.round((elapsed / pct) * (100 - pct))) : null;

  return (
    <div className="mt-2 rounded-lg border border-stone-800 bg-stone-900/50 px-3 py-2.5">
      <div className="flex items-center justify-between gap-2">
        <span className="truncate text-[10px] text-stone-300">
          {last ? last.label : 'Starting the parse engine…'}
          {last?.detail ? <span className="text-stone-500"> — {last.detail}</span> : null}
        </span>
        <span className="shrink-0 font-mono text-[10px] text-stone-500">
          {elapsed.toFixed(0)}s{eta !== null ? ` · ~${eta}s left (est.)` : ''}
        </span>
      </div>
      <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-stone-800">
        <div
          className="h-full rounded-full bg-amber-400 transition-all duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>
      {stages.length > 1 && (
        <div className="mt-1 text-[9px] text-stone-600">
          {stages.length} engine stages completed
          {snap?.error ? <span className="text-red-400"> · {snap.error}</span> : null}
        </div>
      )}
    </div>
  );
}

function fmtConflictValue(value: unknown): string {
  if (value == null || value === '') return '—';
  if (typeof value === 'object') {
    const u = value as { count?: number; kind?: string; value?: unknown };
    if (typeof u.count === 'number') return `${u.count.toLocaleString()} ${u.kind ?? ''}`.trim();
    if (u.value != null && typeof u.value !== 'object') return String(u.value);
    try {
      return JSON.stringify(value);
    } catch {
      return 'unreadable value';
    }
  }
  if (typeof value === 'number') return value.toLocaleString();
  return String(value);
}

function ConflictsPanel({
  conflicts,
  onOpenSource,
}: {
  conflicts: ParseConflict[];
  onOpenSource?: (source: string, anchor: SourceAnchor) => void;
}) {
  if (!conflicts.length) return null;
  return (
    <div className="panel border-red-400/25">
      <div className="panel-header">
        <div className="flex items-center gap-1.5">
          <AlertTriangle size={11} className="text-red-400" />
          <span className="panel-header-title text-red-300">
            Source Conflicts — human decision required
          </span>
        </div>
        <span className="text-[10px] text-stone-600">{conflicts.length}</span>
      </div>
      <div className="panel-body space-y-2">
        {conflicts.map((c, i) => (
          <div key={i} className="rounded-lg border border-red-400/15 bg-red-400/[0.04] px-2.5 py-2">
            <div className="text-[11px] font-medium text-stone-200 font-mono">{c.field}</div>
            <div className="mt-1 space-y-0.5">
              {c.values.map((v, j) => (
                <div key={j} className="flex items-center gap-2 text-[10px]">
                  <span className="text-stone-300 font-mono">{fmtConflictValue(v.value)}</span>
                  <span className="text-stone-600">←</span>
                  {onOpenSource ? (
                    <button
                      type="button"
                      onClick={() => onOpenSource(v.source, conflictAnchor(v))}
                      className="text-stone-500 underline decoration-dotted underline-offset-2 hover:text-amber-300 transition-colors text-left"
                      title="Open the source and highlight where this value was read"
                    >
                      {v.source}{v.lineNo ? ` · line ${v.lineNo}` : ''}
                    </button>
                  ) : (
                    <span className="text-stone-500">{v.source}</span>
                  )}
                </div>
              ))}
            </div>
            <p className="mt-1 text-[10px] text-red-300/80">{c.resolution}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Email-thread linkage + proposed loan updates (never auto-applied)
// ---------------------------------------------------------------------------

const THREAD_BASIS_LABEL: Record<string, string> = {
  in_reply_to_header: 'linked · reply headers',
  references_header: 'linked · references header',
  subject_participants: 'linked · subject match — confirm',
};

function threadFieldLabel(field: string): string {
  return field
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (m) => m.toUpperCase());
}


const toSnake = (k: string) => k.replace(/[A-Z]/g, (m) => `_${m.toLowerCase()}`);

function ThreadPanel({
  ingestionId,
  thread,
  pendingRevisions = [],
  createdDealId = null,
  onOpenDeal,
  onApplied,
}: {
  ingestionId: string;
  thread: ThreadInfo;
  /** The deal this record became, if any — an email a person made its own
   *  deal revises nothing on the thread's first deal (nd-008). */
  createdDealId?: string | null;
  /** Figures this message states that wait for a person (rule R5). */
  pendingRevisions?: PendingRevision[];
  onOpenDeal?: (loanId: string) => void;
  onApplied?: () => void;
}) {
  const [chosen, setChosen] = useState<Record<string, boolean>>({});
  const [applying, setApplying] = useState(false);
  const [applyError, setApplyError] = useState<string | null>(null);
  const [updates, setUpdates] = useState(thread.loanUpdates ?? null);
  const [waiting, setWaiting] = useState(pendingRevisions);

  if (!thread.linkBasis && !updates) return null;

  const waitingWhy = new Map(waiting.map((p) => [toSnake(p.field), p.why]));
  const appliedMap = updates?.applied ?? {};
  const keptMap = updates?.kept ?? {};
  const restated = new Set((updates?.restated ?? []).map(toSnake));
  const superseded = supersededNotes(updates);
  const appliedKeys = new Set([...Object.keys(appliedMap), ...Object.keys(keptMap)].map(toSnake));
  const proposals = updates
    ? [
        ...(updates.conflicts ?? []).map((c) => ({
          field: c.field, kind: 'conflict' as const,
          from: c.current, to: c.incoming, basis: c.basis, source: c.source,
        })),
        ...(updates.additions ?? []).map((a) => ({
          field: a.field, kind: 'addition' as const,
          from: null as unknown, to: a.value, basis: undefined as string | undefined, source: a.source,
        })),
      ]
    : [];
  const separatedAs = threadSeparatedAs(updates, createdDealId);
  const pending = separatedAs ? [] : proposals.filter((p) => !appliedKeys.has(toSnake(p.field)));
  const selectedFields = pending.filter((p) => chosen[p.field]).map((p) => p.field);

  const apply = async (mode: 'apply' | 'keep' = 'apply') => {
    if (!selectedFields.length) return;
    setApplying(true);
    setApplyError(null);
    try {
      const r = mode === 'keep'
        ? await ingestionService.applyToLoan(ingestionId, [], undefined, selectedFields)
        : await ingestionService.applyToLoan(ingestionId, selectedFields);
      setUpdates(r.loanUpdates);
      setWaiting((prev) => prev.filter((p) => !selectedFields.includes(p.field)));
      setChosen({});
      onApplied?.();
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setApplyError(typeof detail === 'string' ? detail : 'Could not apply the updates. Please retry.');
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="panel border-blue-400/20">
      <div className="panel-header">
        <div className="flex items-center gap-1.5">
          <GitBranch size={11} className="text-blue-400" />
          <span className="panel-header-title text-blue-300">Email Thread</span>
        </div>
        <span className="text-[9px] font-mono uppercase text-stone-500">
          {thread.linkBasis ? THREAD_BASIS_LABEL[thread.linkBasis] ?? thread.linkBasis : 'thread root'}
        </span>
      </div>
      <div className="panel-body space-y-2">
        <div className="flex items-center gap-2 text-[10px] text-stone-500">
          <span>
            Message {thread.priorMessages + 1} of this thread
            {thread.linkedTo ? <> · linked to <span className="font-mono text-stone-400">{thread.linkedTo}</span></> : null}
          </span>
        </div>
        {(thread.warnings ?? []).map((w, i) => (
          <div key={i} className="flex items-start gap-1.5 text-[10px] text-amber-300/90">
            <AlertTriangle size={10} className="mt-0.5 shrink-0" />
            <span>{w}</span>
          </div>
        ))}

        {updates && (
          <div className="rounded-lg border border-blue-400/15 bg-blue-400/[0.04] px-2.5 py-2 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-medium text-stone-200">
                Updates for loan{' '}
                {onOpenDeal ? (
                  <button
                    type="button"
                    onClick={() => onOpenDeal(updates.loanId)}
                    className="font-mono text-blue-300 underline decoration-dotted hover:text-blue-200"
                    title="Open the deal these updates belong to"
                  >
                    {updates.loanId}
                  </button>
                ) : (
                  <span className="font-mono text-blue-300">{updates.loanId}</span>
                )}
                {updates.loanPropertyName ? <span className="text-stone-500"> — {updates.loanPropertyName}</span> : null}
              </span>
              <span className="text-[9px] uppercase font-mono text-stone-500">
                {separatedAs ? 'separate deal' : waiting.length ? `${waiting.length} waiting` : updates.status}
              </span>
            </div>

            {separatedAs ? (
              <p className="text-[10px] text-stone-400" data-testid="thread-separated">
                This email became its own deal{' '}
                <span className="font-mono text-amber-300">{separatedAs}</span> — nothing in it
                revises {updates.loanId}. What it proposed stays on this record for the audit trail.
              </p>
            ) : pending.length === 0 && Object.keys(appliedMap).length === 0 && (
              <p className="text-[10px] text-stone-500">
                No new field values versus the loan as it stands — this message adds no changes.
              </p>
            )}

            {pending.map((p) => (
              <label key={p.field} className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!chosen[p.field]}
                  disabled={!!updates.loanKilled}
                  onChange={(e) => setChosen((prev) => ({ ...prev, [p.field]: e.target.checked }))}
                  className="mt-0.5 accent-amber-400 disabled:opacity-40"
                />
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-[11px]">
                    <span className="text-stone-300">{threadFieldLabel(p.field)}</span>
                    {p.kind === 'conflict' ? (
                      <span className="font-mono">
                        <span className="text-stone-500 line-through">{fmtThreadValue(p.field, p.from)}</span>
                        <span className="text-stone-600"> → </span>
                        <span className="text-amber-300">{fmtThreadValue(p.field, p.to)}</span>
                      </span>
                    ) : (
                      <span className="font-mono text-emerald-300">+ {fmtThreadValue(p.field, p.to)}</span>
                    )}
                  </div>
                  <p className="text-[9px] text-stone-500">
                    {waitingWhy.has(toSnake(p.field)) ? (
                      <span className="text-amber-300/90">Waiting — {waitingWhy.get(toSnake(p.field))}</span>
                    ) : restated.has(toSnake(p.field)) ? (
                      'restated from the earlier email — not a revision'
                    ) : superseded.has(toSnake(p.field)) ? (
                      `${superseded.get(toSnake(p.field))} — not applied`
                    ) : p.kind === 'conflict' ? (p.basis ?? 'disagrees with the loan') : 'not on the loan yet'}
                    {p.source ? ` · from ${p.source}` : ''}
                  </p>
                </div>
              </label>
            ))}

            {Object.keys(appliedMap).length > 0 && (
              <div className="space-y-0.5">
                {Object.entries(appliedMap).map(([k, v]) => (
                  <div key={k} className="flex items-center gap-1.5 text-[10px] text-emerald-400">
                    <CheckCircle2 size={10} className="shrink-0" />
                    <span>
                      {threadFieldLabel(k)} {v.automatic ? 'reached the deal on arrival' : 'applied'}:{' '}
                      <span className="font-mono">{fmtThreadValue(k, v.from)} → {fmtThreadValue(k, v.to)}</span>
                    </span>
                  </div>
                ))}
                {Object.entries(keptMap).map(([k, v]) => (
                  <div key={k} className="flex items-center gap-1.5 text-[10px] text-stone-400">
                    <CheckCircle2 size={10} className="shrink-0" />
                    <span>
                      {threadFieldLabel(k)} kept at <span className="font-mono">{fmtThreadValue(k, v.kept)}</span>
                      {' '}— declined {fmtThreadValue(k, v.declined)}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {pending.length > 0 && updates.loanKilled && (
              <div className="flex items-start gap-1.5 pt-1 text-[10px] text-red-300">
                <XCircle size={11} className="mt-0.5 shrink-0" />
                <span>
                  Deal {updates.loanId} has been killed — nothing from this email is written to a
                  dead deal. Reopen it from the deal file if the deal is coming back, or create a new
                  deal from this record.
                </span>
              </div>
            )}
            {pending.length > 0 && !updates.loanKilled && (
              <div className="flex flex-wrap items-center gap-2 pt-1">
                <button
                  onClick={() => apply('apply')}
                  disabled={applying || selectedFields.length === 0}
                  className="btn-primary text-[10px] px-3 py-1.5 disabled:opacity-50"
                >
                  {applying
                    ? 'Applying…'
                    : `Apply ${selectedFields.length || ''} selected to ${updates.loanId}`}
                </button>
                <button
                  onClick={() => apply('keep')}
                  disabled={applying || selectedFields.length === 0}
                  className="btn-secondary text-[10px] px-3 py-1.5 disabled:opacity-50"
                  title="Keep the deal's value for the selected fields — the decision is recorded"
                >
                  Keep deal value
                </button>
                <span className="text-[9px] text-stone-600">
                  Figures the deal's sender states afresh reach the deal on arrival; what is
                  listed here waits for you. Either choice is written and audited.
                </span>
              </div>
            )}
            {applyError && (
              <p className="text-[10px] text-red-300">{applyError}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}


function ReingestPanel({
  ingestionId,
  reingest,
  ownDealId,
  onApplied,
}: {
  ingestionId: string;
  reingest: ReingestUpdates;
  /** The deal this record has already become, if any. */
  ownDealId?: string | null;
  onApplied?: () => void;
}) {
  // Newest-wins is the default: pending fields start checked (apply the new
  // value); unchecking keeps the deal's current value. Nothing writes until
  // Apply is pressed — the default is still subject to confirmation.
  const [chosen, setChosen] = useState<Record<string, boolean>>({});
  const [applying, setApplying] = useState(false);
  const [applyError, setApplyError] = useState<string | null>(null);
  const [updates, setUpdates] = useState(reingest);
  // A record that already became a deal of its own is not folded into another
  // one: resolving would overwrite that deal and move the record's documents
  // onto it, and the server refuses (stress test PKG-01).
  const becameOwnDeal = Boolean(ownDealId) && ownDealId !== updates.loanId;

  const appliedMap = updates.applied ?? {};
  const appliedKeys = new Set(Object.keys(appliedMap).map(toSnake));
  const proposals = [
    ...(updates.conflicts ?? []).map((c) => ({
      field: c.field, kind: 'conflict' as const,
      from: c.current, to: c.incoming, basis: c.basis, source: c.incomingSource ?? c.source,
    })),
    ...(updates.additions ?? []).map((a) => ({
      field: a.field, kind: 'addition' as const,
      from: null as unknown, to: a.value, basis: undefined as string | undefined,
      source: a.incomingSource ?? a.source,
    })),
  ];
  const pending = proposals.filter((p) => !appliedKeys.has(toSnake(p.field)));

  const apply = async () => {
    if (!pending.length || becameOwnDeal) return;
    setApplying(true);
    setApplyError(null);
    try {
      const resolutions = pending.map((p) => ({
        field: p.field,
        choice: (chosen[p.field] ?? true ? 'new' : 'current') as 'new' | 'current',
      }));
      const r = await ingestionService.resolveReingest(ingestionId, updates.loanId, resolutions);
      setUpdates(r.reingest);
      setChosen({});
      onApplied?.();
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setApplyError(typeof detail === 'string' ? detail : 'Could not resolve the re-ingest. Please retry.');
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="panel border-purple-400/20">
      <div className="panel-header">
        <div className="flex items-center gap-1.5">
          <Link2 size={11} className="text-purple-400" />
          <span className="panel-header-title text-purple-300">Re-ingest — matched to an existing deal</span>
        </div>
        <span className="text-[9px] font-mono uppercase text-stone-500">
          {reingestBasisLabel(updates.matchBasis, updates.controlId)}
        </span>
      </div>
      <div className="panel-body space-y-2">
        <div className="rounded-lg border border-purple-400/15 bg-purple-400/[0.04] px-2.5 py-2 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-medium text-stone-200">
              Matches deal <span className="font-mono text-purple-300">{updates.loanId}</span>
              {updates.loanPropertyName ? <span className="text-stone-500"> — {updates.loanPropertyName}</span> : null}
            </span>
            <span className="text-[9px] uppercase font-mono text-stone-500">{updates.status}</span>
          </div>

          {pending.length === 0 && Object.keys(appliedMap).length === 0 && (
            <p className="text-[10px] text-stone-500">
              This re-ingest matches the deal but changes nothing versus its current values.
            </p>
          )}

          {pending.map((p) => (
            <label key={p.field} className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={chosen[p.field] ?? true}
                onChange={(e) => setChosen((prev) => ({ ...prev, [p.field]: e.target.checked }))}
                disabled={becameOwnDeal}
                className="mt-0.5 accent-purple-400"
              />
              <div className="min-w-0">
                <div className="flex items-center gap-2 text-[11px]">
                  <span className="text-stone-300">{threadFieldLabel(p.field)}</span>
                  {p.kind === 'conflict' ? (
                    <span className="font-mono">
                      <span className="text-stone-500 line-through">{fmtThreadValue(p.field, p.from)}</span>
                      <span className="text-stone-600"> → </span>
                      <span className="text-purple-300">{fmtThreadValue(p.field, p.to)}</span>
                    </span>
                  ) : (
                    <span className="font-mono text-emerald-300">+ {fmtThreadValue(p.field, p.to)}</span>
                  )}
                </div>
                <p className="text-[9px] text-stone-500">
                  {p.kind === 'conflict'
                    ? `current ${updates.currentSource ? `(${updates.currentSource})` : ''} vs ${p.source ?? 're-ingest'}`
                    : `not on the deal yet · from ${p.source ?? 're-ingest'}`}
                </p>
              </div>
            </label>
          ))}

          {Object.keys(appliedMap).length > 0 && (
            <div className="space-y-0.5">
              {Object.entries(appliedMap).map(([k, v]) => (
                <div key={k} className="flex items-center gap-1.5 text-[10px] text-emerald-400">
                  <CheckCircle2 size={10} className="shrink-0" />
                  <span>
                    {threadFieldLabel(k)}{' '}
                    {v.choice === 'current' ? (
                      <>kept current: <span className="font-mono">{fmtThreadValue(k, v.from)}</span></>
                    ) : (
                      <>updated: <span className="font-mono">{fmtThreadValue(k, v.from)} → {fmtThreadValue(k, v.to)}</span></>
                    )}
                  </span>
                </div>
              ))}
            </div>
          )}

          {pending.length > 0 && becameOwnDeal && (
            <p data-testid="reingest-resolve-refused" className="pt-1 text-[10px] text-amber-300">
              Resolve is not offered: this record already became its own deal, {ownDealId}, so it cannot
              also be reconciled into {updates.loanId}.
            </p>
          )}
          {pending.length > 0 && !becameOwnDeal && (
            <div className="flex items-center gap-2 pt-1">
              <button
                onClick={apply}
                disabled={applying}
                className="btn-primary text-[10px] px-3 py-1.5 disabled:opacity-50"
              >
                {applying ? 'Applying…' : `Resolve to ${updates.loanId}`}
              </button>
              <span className="text-[9px] text-stone-600">
                Newest-wins by default — uncheck to keep the deal's current value. The prior value is retained.
              </span>
            </div>
          )}
          {applyError && <p className="text-[10px] text-red-300">{applyError}</p>}
        </div>
      </div>
    </div>
  );
}


function ScreenResultsPanel({ screens }: { screens: ScreenResults }) {
  return (
    <div className="panel border-amber-400/20">
      <div className="panel-header">
        <div className="flex items-center gap-1.5">
          <ShieldCheck size={11} className="text-amber-400" />
          <span className="panel-header-title">Credit Box Screen</span>
        </div>
        <span className="text-[9px] text-stone-600 font-mono">
          deterministic · rules-as-data
        </span>
      </div>
      <div className="panel-body space-y-3">
        {screens.status === 'not_run' ? (
          <div>
            <p className="text-[11px] text-stone-400">{screens.reason}</p>
            <div className="mt-2 space-y-1.5">
              {(screens.missing ?? []).map((m, i) => (
                <div key={i} className="flex items-start gap-2 text-[10px]">
                  <XCircle size={10} className="text-red-400 mt-0.5 shrink-0" />
                  <div>
                    <span className="font-mono text-red-300">{m.field}</span>
                    <span className="text-stone-500"> — {m.why}. </span>
                    <span className="text-stone-400">{m.fillHint}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          (screens.runs ?? []).map((run) => (
            <div key={run.program} className="rounded-lg border border-amber-400/10 bg-black/20 px-2.5 py-2">
              <div className="flex items-center gap-2">
                <span className="text-[11px] font-medium text-stone-200">{run.programName}</span>
                {screenOutcomeBadge(run.outcome)}
                <span className="ml-auto text-[9px] font-mono text-stone-600" title={`rules sha256 ${run.rulesSha256}`}>
                  v{run.rulesVersion} · {run.rulesSha256.slice(0, 8)}
                </span>
              </div>
              <div className="mt-1.5 space-y-0.5">
                {run.checks.filter((c) => c.outcome !== 'REFER').map((c, i) => (
                  <div key={i} className="flex items-start gap-2 text-[10px]">
                    {c.outcome === 'PASS' ? (
                      <CheckCircle2 size={10} className="text-emerald-400 mt-0.5 shrink-0" />
                    ) : c.outcome === 'EXCEPTION' ? (
                      <AlertTriangle size={10} className="text-amber-400 mt-0.5 shrink-0" />
                    ) : (
                      <XCircle size={10} className="text-red-400 mt-0.5 shrink-0" />
                    )}
                    <span className="font-mono text-stone-400 w-[168px] shrink-0">{c.name}</span>
                    <span className="text-stone-500 break-all">
                      {checkDetail(c.detail)}
                      {/* What the ratio was computed on, and whether that is a
                          stand-in for a stabilized figure (SCR-01). */}
                      {checkBasis(c, run) ? <span className="block text-[9px] text-stone-600">{checkBasis(c, run)}</span> : null}
                    </span>
                  </div>
                ))}
              </div>
              {run.missingInputs.length > 0 && (
                <div className="mt-1.5 border-t border-red-400/10 pt-1.5 space-y-0.5">
                  {run.missingInputs.map((m, i) => (
                    <div key={i} className="flex items-start gap-2 text-[10px]">
                      <XCircle size={10} className="text-red-400 mt-0.5 shrink-0" />
                      <span className="text-red-300 font-mono">{m.field}</span>
                      <span className="text-stone-500">{m.fillHint}</span>
                    </div>
                  ))}
                </div>
              )}
              <p className="mt-1.5 text-[9px] text-stone-600">
                Judgment factors ({run.referrals.length}) route to REFER — humans decide; the screen never auto-declines.
              </p>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function ModelChangePanel({
  ingestionId,
  attachment,
}: {
  ingestionId: string;
  attachment: AttachmentReport;
}) {
  const [chosen, setChosen] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState<'one-off' | 'adopt' | null>(null);
  const [outcome, setOutcome] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const diff = attachment.baselineDiff ?? [];
  if (!diff.length || !attachment.sha256) return null;
  const key = (d: BaselineDiffEntry) => `${d.sheet}!${d.labelCell}`;
  const selected = diff.filter((d) => chosen[key(d)]);

  const run = async (mode: 'one-off' | 'adopt') => {
    if (!selected.length || !attachment.sha256) return;
    setBusy(mode);
    setErr(null);
    try {
      if (mode === 'one-off') {
        await ingestionService.modelChangeOneOff(ingestionId, attachment.sha256, selected);
        setOutcome(
          `${selected.length} line(s) applied to this deal only — flagged ad hoc, ` +
          'template unchanged.',
        );
      } else {
        const r = await ingestionService.modelChangeAdopt(ingestionId, attachment.sha256, selected);
        setOutcome(
          `Draft ${r.templateName} v${r.version} created — ${r.next}`,
        );
      }
      setChosen({});
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setErr(typeof detail === 'string' ? detail : 'Action failed. Please retry.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="mt-1.5 rounded-lg border border-amber-400/20 bg-amber-400/[0.04] px-2.5 py-2 space-y-1.5">
      <p className="text-[10px] text-amber-300">
        {diff.length} labeled line(s) not in the ratified template — choose how to handle them.
      </p>
      {diff.map((d) => (
        <label key={key(d)} className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={!!chosen[key(d)]}
            onChange={(e) => setChosen((p) => ({ ...p, [key(d)]: e.target.checked }))}
            className="mt-0.5 accent-amber-400"
          />
          <span className="text-[10px] text-stone-300">
            <span className="font-mono text-stone-200">{d.label}</span>
            <span className="text-stone-500"> → {d.metric}</span>
            <span className="text-stone-600 font-mono"> · {d.sheet}!{d.labelCell}</span>
            {d.valuePreview != null && (
              <span className="text-stone-500"> · value {d.valuePreview}</span>
            )}
          </span>
        </label>
      ))}
      <div className="flex items-center gap-2 pt-0.5">
        <button
          onClick={() => run('adopt')}
          disabled={!!busy || !selected.length}
          title="Creates the next template version as a draft; the governed approval chain must complete before parsing trusts it"
          className="btn-primary text-[10px] px-3 py-1.5 disabled:opacity-50"
        >
          {busy === 'adopt' ? 'Creating draft…' : 'Adopt into template'}
        </button>
        <button
          onClick={() => run('one-off')}
          disabled={!!busy || !selected.length}
          title="Binds the values to this deal only — flagged ad hoc; never promotes into the template"
          className="btn-secondary text-[10px] px-3 py-1.5 disabled:opacity-50"
        >
          {busy === 'one-off' ? 'Applying…' : 'Apply one-off to this deal'}
        </button>
        <span className="text-[9px] text-stone-600">Ad hoc lines never auto-promote.</span>
      </div>
      {outcome && <p className="text-[10px] text-emerald-400">{outcome}</p>}
      {err && <p className="text-[10px] text-red-300">{err}</p>}
    </div>
  );
}


function AttachmentReportList({
  attachments,
  ingestionId,
  onOpenSource,
}: {
  attachments: AttachmentReport[];
  ingestionId?: string;
  onOpenSource?: (sha256: string) => void;
}) {
  if (!attachments.length) return null;
  return (
    <div className="panel">
      <div className="panel-header">
        <div className="flex items-center gap-1.5">
          <Paperclip size={11} className="text-stone-500" />
          <span className="panel-header-title">Attachment Processing</span>
        </div>
      </div>
      <div className="panel-body space-y-1.5">
        {attachments.map((a, i) => {
          const openable = Boolean(ingestionId && a.sha256 && onOpenSource);
          return (
          <div key={i} className="rounded-lg border border-amber-400/10 bg-black/20 px-2.5 py-2">
            <div className="flex items-center gap-2">
              {a.kind === 'excel' ? <FileSpreadsheet size={11} className="text-emerald-400 shrink-0" /> :
               a.kind === 'pdf' ? <FileText size={11} className="text-red-400 shrink-0" /> :
               <FileText size={11} className="text-blue-400 shrink-0" />}
              {openable ? (
                <button
                  type="button"
                  onClick={() => onOpenSource!(a.sha256!)}
                  className="text-[11px] text-amber-300 truncate hover:text-amber-200 text-left flex-1 min-w-0"
                  title="Open stored original in the source viewer"
                >
                  {a.filename}
                </button>
              ) : (
                <span className="text-[11px] text-stone-300 truncate flex-1">{a.filename}</span>
              )}
              {openable && <Eye size={11} className="text-stone-600 shrink-0" />}
              <span className="ml-auto shrink-0"><AttachmentStatusBadge status={a.status} /></span>
            </div>
            {a.archive ? (
              <ArchiveOutcome archive={a.archive} />
            ) : (
              <p className="mt-1 text-[10px] text-stone-500">{a.detail}</p>
            )}
            {a.template && (
              <p className="mt-0.5 text-[9px] font-mono text-blue-300">
                {a.template.name} v{a.template.version} · {a.template.client}
              </p>
            )}
            {ingestionId && a.status === 'needs_model_onboarding' && a.sha256 && (
              <OnboardingSweepButton ingestionId={ingestionId} sha256={a.sha256} />
            )}
            {ingestionId && <ModelChangePanel ingestionId={ingestionId} attachment={a} />}
          </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Upload Panel — file drag-and-drop ingestion (Items 2, 3)
// ---------------------------------------------------------------------------

// Email containers delineate themselves — a message is a request. Loose
// documents do not, so the operator can override the engine's judgment when
// they already know what they dropped.
const EMAIL_EXTS = new Set(['eml', 'msg']);

const GROUPING_CHOICES: { mode: GroupingMode; label: string; hint: string }[] = [
  {
    mode: 'auto',
    label: 'Decide from content',
    hint: 'Files are grouped only when they name the same property, share a deal filename, or agree on amount and location.',
  },
  {
    mode: 'single',
    label: 'One deal package',
    hint: 'Treat every dropped document as one request — an OM, rent roll and T12 for the same property.',
  },
  {
    mode: 'separate',
    label: 'Separate requests',
    hint: 'Every document is its own request, with no grouping applied.',
  },
];

export function UploadPanel({ demo }: { demo: boolean }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const openDossier = useDealDossierStore((s) => s.openDossier);
  const [files, setFiles] = useState<File[]>([]);
  const [parsing, setParsing] = useState(false);
  const [progressJobId, setProgressJobId] = useState<string | null>(null);
  const [converting, setConverting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<UploadResult[]>([]);
  const [activeResult, setActiveResult] = useState(0);
  const [demoParsed, setDemoParsed] = useState(false);
  const [broker, setBroker] = useState('');
  const [grouping, setGrouping] = useState<GroupingMode>('auto');
  const [threadBlocked, setThreadBlocked] = useState(false);
  const [viewerSha, setViewerSha] = useState<string | null>(null);
  const [viewerRecordId, setViewerRecordId] = useState<string | null>(null);
  // Convert refuses a name nobody confirmed when the record suggests a better
  // one or matches a killed deal (nd-037 / nd3-19); the person confirms here.
  const [confirmNew, setConfirmNew] = useState<(NewDealConfirmation & { forceNewLoan: boolean }) | null>(null);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const reset = () => {
    setFiles([]);
    setResults([]);
    setActiveResult(0);
    setDemoParsed(false);
    setError(null);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const dropped = Array.from(e.dataTransfer.files);
    setFiles((prev) => [...prev, ...dropped]);
    setResults([]);
    setDemoParsed(false);
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFiles(Array.from(e.target.files));
      setResults([]);
      setDemoParsed(false);
    }
  };

  const handleParse = async () => {
    if (!files.length) return;
    setError(null);
    setParsing(true);
    if (demo) {
      // Demo mode: illustrate the flow without hitting the backend.
      setTimeout(() => {
        setParsing(false);
        setDemoParsed(true);
      }, 1200);
      return;
    }
    const jobId = crypto.randomUUID();
    setProgressJobId(jobId);
    try {
      const r = await ingestionService.uploadPackage(files, broker || undefined, grouping, jobId);
      setResults(r.ingestions);
      setActiveResult(0);
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(
        typeof detail === 'string'
          ? detail
          : 'Could not parse the upload. Check that the API is reachable and the file types are supported (.msg, .eml, .xlsx, .csv, .pdf, .docx).',
      );
    } finally {
      setParsing(false);
      setProgressJobId(null);
    }
  };

  const result = results[activeResult]?.record ?? results[0]?.record ?? null;
  const isDuplicate = results[activeResult]?.duplicate ?? false;
  const activeEntry = results[activeResult] ?? results[0] ?? null;
  const duplicateNotice = activeEntry ? duplicateUploadNotice(activeEntry) : null;

  // Duplicate-content escape hatch: archive the stale record (flagged, never
  // deleted — recoverable by id) and immediately re-ingest the same files so
  // the current engine produces a fresh parse.
  const handleArchiveAndReparse = async () => {
    if (!result || !files.length) return;
    setError(null);
    setParsing(true);
    try {
      await ingestionService.archive(result.id);
      const r = await ingestionService.uploadPackage(files, broker || undefined, grouping);
      setResults(r.ingestions);
      queryClient.invalidateQueries({ queryKey: ['ingestion-queue'] });
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Could not archive and re-parse. Please retry.');
    } finally {
      setParsing(false);
    }
  };

  const handleCreateDeal = async (forceNewLoan = false, overrides?: ConvertPayload['overrides']) => {
    if (demo) {
      navigate('/pipeline');
      return;
    }
    if (!result) return;
    setConverting(true);
    setError(null);
    setThreadBlocked(false);
    try {
      // A person creating the deal: held to the loan purpose, city + state, initial
      // and fully funded amounts (client item #1). The mail pipeline never is.
      const converted = await ingestionService.convert(result.id, {
        priority: result.aiPriority,
        forceNewLoan,
        requireIntakeFields: true,
        overrides,
      });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['ingestion-queue'] }),
        queryClient.invalidateQueries({ queryKey: ['loans'] }),
      ]);
      if (converted.loan?.id) openDossier(converted.loan.id);
      else navigate('/pipeline');
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
      // Not a failure to retry: the name the record is held for needs a
      // person's confirmation first, and a killed deal may be at stake.
      const refusal = confirmationFromRefusal(detail);
      if (refusal) {
        setConfirmError(null);
        setConfirmNew({ ...refusal, forceNewLoan });
        return;
      }
      // The deal cannot be created until the four fields required at intake are
      // there: the dialog asks for each one the server named.
      const gaps = requiredGapsFromRefusal(detail);
      if (gaps) {
        setConfirmError(null);
        setConfirmNew((prev) => ({
          needsName: false, suggested: null, subjectName: null, killed: [],
          ...(prev ?? {}), missing: gaps, forceNewLoan,
        }));
        return;
      }
      if (typeof detail === 'string' && detail.includes('force_new_loan')) {
        setThreadBlocked(true);
      }
      setError(typeof detail === 'string' ? detail : 'Could not create the deal record. Please retry.');
    } finally {
      setConverting(false);
    }
  };

  // The confirmed name is the person's review decision (resolve-name),
  // which creates the deal only when an administrator has turned automatic
  // creation on (it is off by default); otherwise convert runs again with the
  // name now confirmed. Either way the deal is held to the four
  // fields required at intake, and the answers to any still missing ride along.
  const confirmUploadName = async (name: string | null, overrides?: ConvertPayload['overrides']) => {
    if (!result || !confirmNew) return;
    const { forceNewLoan } = confirmNew;
    if (name === null) {
      setConfirmNew(null);
      await handleCreateDeal(forceNewLoan, overrides);
      return;
    }
    setConverting(true);
    setConfirmError(null);
    try {
      const record = await ingestionService.resolveDealName(result.id, name, undefined, {
        requireIntakeFields: true,
        overrides,
      });
      setConfirmNew(null);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['ingestion-queue'] }),
        queryClient.invalidateQueries({ queryKey: ['loans'] }),
      ]);
      if (record?.convertedToLoanId) openDossier(record.convertedToLoanId);
      else await handleCreateDeal(forceNewLoan, overrides);
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
      const gaps = requiredGapsFromRefusal(detail);
      if (gaps) {
        // Nothing was recorded: the dialog stays open and asks for what is missing.
        setConfirmNew((prev) => (prev ? { ...prev, missing: gaps } : prev));
        return;
      }
      setConfirmError(typeof detail === 'string' ? detail : 'Could not record the name. Please retry.');
    } finally {
      setConverting(false);
    }
  };

  const parsed = demo ? demoParsed : !!result;
  const sourceFile = files[0]?.name || 'upload';
  // Only loose documents are groupable; the control stays hidden otherwise.
  const looseDocCount = files.filter(
    (f) => !EMAIL_EXTS.has(f.name.split('.').pop()?.toLowerCase() || ''),
  ).length;
  const documentGrouping = !demo ? result?.parseReport?.documentGrouping ?? null : null;

  const mockResult = {
    property: 'Riverfront Commerce Center',
    type: 'Industrial',
    location: 'Phoenix, AZ',
    loanAmount: '$38.5M',
    ltv: '68%',
    dscr: '1.44x',
    occupancy: '96%',
    sponsor: 'Desert Commerce Partners',
    noi: '$2.6M',
    tracedFields: ['EGR (C14)', 'Expenses (C22)', 'NOI (C28)', 'Loan Amount (B4)', 'LTV (D8)'],
    confidence: 87,
  };

  // Live: deterministic field coverage (found core fields ÷ core fields) —
  // a completeness measure, not a probabilistic confidence score.
  const coverage = demo ? mockResult.confidence : Math.round((result?.extractionConfidence ?? 0) * 100);

  const rows = demo
    ? [
        { label: 'Property', value: mockResult.property },
        { label: 'Asset Type', value: mockResult.type },
        { label: 'Location', value: mockResult.location },
        { label: 'Loan Amount', value: mockResult.loanAmount },
        { label: 'LTV', value: mockResult.ltv },
        { label: 'DSCR', value: mockResult.dscr },
        { label: 'Occupancy', value: mockResult.occupancy },
        { label: 'Sponsor', value: mockResult.sponsor },
        { label: 'NOI', value: mockResult.noi },
      ]
    : [
        { label: 'Property', value: result?.extractedPropertyName ?? '—' },
        { label: 'Asset Type', value: result?.extractedAssetType ?? '—' },
        { label: 'Location', value: result?.extractedLocation ?? '—' },
        { label: 'Loan Amount', value: fmtAmountM(result?.extractedLoanAmount) },
        { label: 'LTV', value: pctOrDash(result?.extractedLtv) },
        { label: 'DSCR', value: result?.extractedDscr != null ? `${result.extractedDscr.toFixed(2)}x` : '—' },
        { label: 'Occupancy', value: pctOrDash(result?.extractedOccupancy) },
        { label: 'Sponsor', value: result?.extractedSponsor ?? '—' },
        { label: 'Cap Rate', value: pctOrDash(result?.extractedCapRate) },
      ];

  const tracedFields: string[] = demo
    ? mockResult.tracedFields
    : result?.fieldMapping
      ? Object.entries(result.fieldMapping).map(
          ([f, ref]) => `${f}${ref.cellRef ? ` (${ref.cellRef})` : ''} ← ${ref.sourceDoc ?? '—'}`,
        )
      : [];
  const liveReport = !demo ? result?.parseReport ?? null : null;
  const liveScreens = !demo ? result?.screenResults ?? null : null;
  const liveEnvelope = !demo ? result?.emailEnvelope ?? null : null;

  return (
    <div className="space-y-3">
      {demo && (
        <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg border border-amber-400/25 bg-amber-400/[0.06]">
          <AlertTriangle size={13} className="text-amber-400 mt-0.5 shrink-0" />
          <div>
            <p className="text-[11px] font-medium text-amber-300">Demo preview — uploads are not saved</p>
            <p className="text-[10px] text-stone-500 mt-0.5">
              This account is viewing illustrative data. Files stay in this browser and no deal record is created.
            </p>
          </div>
        </div>
      )}
      {/* Inbound-only automation notice (Item 4) */}
      <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg border border-amber-400/20 bg-amber-400/[0.04]">
        <Ban size={13} className="text-amber-400 mt-0.5 shrink-0" />
        <div>
          <p className="text-[11px] font-medium text-amber-300">Inbound-Only Automation</p>
          <p className="text-[10px] text-stone-500 mt-0.5">
            Processing of inbound materials is automated. All outbound broker and borrower communication
            requires <span className="text-amber-400 font-medium">human approval</span> — no automated replies are sent.
          </p>
        </div>
      </div>

      {/* Broker field */}
      <div className="panel">
        <div className="panel-header">
          <span className="panel-header-title">Broker / Source</span>
        </div>
        <div className="panel-body">
          <input
            type="text"
            value={broker}
            onChange={(e) => setBroker(e.target.value)}
            placeholder="Broker name or firm (optional)"
            className="w-full bg-black/30 border border-amber-400/20 rounded-lg px-3 py-1.5 text-[11px] text-stone-200 placeholder-stone-600 focus:outline-none focus:border-amber-400/40"
          />
        </div>
      </div>

      {/* Drop zone */}
      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleDrop}
        className="panel border-2 border-dashed border-amber-400/20 hover:border-amber-400/40 transition-colors cursor-pointer"
        onClick={() => inputRef.current?.click()}
      >
        <div className="panel-body flex flex-col items-center justify-center py-6 text-center">
          <Upload size={24} className="text-stone-600 mb-2" />
          <p className="text-[12px] text-stone-400 font-medium">Drop emails or files here, or click to browse</p>
          <p className="text-[10px] text-stone-600 mt-1">
            Supports: Outlook email (.msg), Email (.eml), Excel (.xlsx / .csv), PDF, Word (.docx)
          </p>
          <p className="text-[10px] text-stone-700 mt-1">
            Non-standard broker templates supported — no standardized form required
          </p>
          {/* Deliberately unfiltered: a message saved out of webmail loses its
              .eml extension, and an accept list would hide it from the picker
              even though the engine identifies it by content. Anything the
              engine cannot parse is stored for human review, never dropped. */}
          <input
            ref={inputRef}
            type="file"
            className="hidden"
            multiple
            onChange={handleFileInput}
          />
        </div>
      </div>

      {files.length > 0 && (
        <div className="panel">
          <div className="panel-header">
            <span className="panel-header-title">Files Queued</span>
          </div>
          <div className="panel-body space-y-1.5">
            {files.map((f, i) => {
              const ext = f.name.split('.').pop()?.toLowerCase() || '';
              // Once parsed, the stored original is viewable. Matching on
              // filename is what links the row you dropped to the record the
              // parser made from it.
              const stored = (() => {
                const refs = results.flatMap((r) =>
                  (r.record.rawFilePaths ?? []).map((ref) => ({ ref, record: r.record })),
                );
                const exact = refs.find(({ ref }) => ref.filename === f.name);
                if (exact) return exact;
                const ci = refs.find(
                  ({ ref }) => ref.filename.localeCompare(f.name, undefined, { sensitivity: 'accent' }) === 0,
                );
                if (ci) return ci;
                // Single-file email drop: the stored email_source is that file
                // even if the filename was rewritten slightly on persist.
                if (files.length === 1 && results.length === 1) {
                  const email = refs.find(({ ref }) => ref.kind === 'email_source');
                  if (email) return email;
                  if (refs.length === 1) return refs[0];
                }
                return undefined;
              })();
              const viewable = Boolean(stored);
              const Row = viewable ? 'button' : 'div';
              return (
                <Row
                  key={i}
                  {...(viewable
                    ? {
                        type: 'button' as const,
                        onClick: () => {
                          setViewerRecordId(stored!.record.id);
                          setViewerSha(stored!.ref.sha256);
                        },
                        title: 'Open the stored original — read the email exactly as it arrived',
                      }
                    : {})}
                  className={`flex w-full items-center gap-2 text-[11px] rounded px-1 py-0.5 text-left ${
                    viewable ? 'hover:bg-white/[0.04] cursor-pointer' : ''
                  }`}
                >
                  {viewable
                    ? <Eye size={10} className="text-amber-400/80" />
                    : <Paperclip size={10} className="text-stone-600" />}
                  <span className={`flex-1 truncate ${viewable ? 'text-stone-200' : 'text-stone-300'}`}>{f.name}</span>
                  {viewable && <span className="text-[9px] text-amber-300/70">view</span>}
                  <span className="text-stone-600 font-mono">{Math.round(f.size / 1024)}KB</span>
                  <span className={`px-1.5 py-0.5 rounded text-[9px] font-mono uppercase ${
                    ext === 'xlsx' || ext === 'xls' || ext === 'csv' ? 'bg-emerald-400/10 text-emerald-400' :
                    ext === 'pdf' ? 'bg-red-400/10 text-red-400' :
                    'bg-blue-400/10 text-blue-400'
                  }`}>{ext}</span>
                </Row>
              );
            })}
            {looseDocCount > 1 && (
              <div className="pt-2 mt-1 border-t border-stone-800/80 space-y-1.5">
                <div className="flex items-center gap-1.5">
                  <GitBranch size={10} className="text-stone-600" />
                  <span className="text-[10px] uppercase tracking-widest font-mono text-stone-500">
                    {looseDocCount} loose documents — how many requests?
                  </span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {GROUPING_CHOICES.map((choice) => (
                    <button
                      key={choice.mode}
                      type="button"
                      onClick={() => setGrouping(choice.mode)}
                      title={choice.hint}
                      aria-pressed={grouping === choice.mode}
                      className={`px-2.5 py-1 rounded-lg border text-[10px] transition-colors ${
                        grouping === choice.mode
                          ? 'border-amber-400/50 bg-amber-400/10 text-amber-300'
                          : 'border-stone-800 text-stone-400 hover:border-stone-600 hover:text-stone-200'
                      }`}
                    >
                      {choice.label}
                    </button>
                  ))}
                </div>
                <p className="text-[10px] text-stone-600">
                  {GROUPING_CHOICES.find((c) => c.mode === grouping)?.hint}
                  {' '}Emails always parse as one request each, whatever this is set to.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {files.length > 0 && !parsed && (
        <>
          <button
            onClick={handleParse}
            disabled={parsing}
            className="btn-primary w-full flex items-center justify-center gap-2 text-[11px] py-2"
          >
            {parsing ? (
              <>
                <div className="w-3 h-3 border-2 border-amber-400/30 border-t-amber-400 rounded-full animate-spin" />
                Parsing documents locally...
              </>
            ) : (
              <>
                <Sparkles size={12} />
                Parse &amp; Extract Deal Data
              </>
            )}
          </button>
          {parsing && !demo && progressJobId && (
            <ParseProgressBar jobId={progressJobId} />
          )}
        </>
      )}

      {/* Extracted result with traceability (Item 3) */}
      {parsed && (
        <div className="space-y-3">
          {results.length > 1 && (
            <div className="flex items-center gap-1.5 flex-wrap px-1">
              <span className="text-[10px] uppercase tracking-widest font-mono text-stone-500 mr-1">
                {results.length} records —
              </span>
              {results.map((r, i) => {
                const thread = r.record.parseReport?.thread;
                const linked = !!thread?.linkBasis;
                return (
                  <button
                    key={r.record.id}
                    onClick={() => { setActiveResult(i); setThreadBlocked(false); setError(null); }}
                    className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-[10px] transition-colors ${
                      i === activeResult
                        ? 'border-amber-400/50 bg-amber-400/10 text-amber-300'
                        : 'border-stone-800 text-stone-400 hover:border-stone-600 hover:text-stone-200'
                    }`}
                    title={r.record.sourceSubject ?? r.record.id}
                  >
                    <span className="font-mono">{i + 1}</span>
                    <span className="max-w-[180px] truncate">{r.record.sourceSubject ?? r.record.id}</span>
                    {linked && (
                      <span className="flex items-center gap-0.5 text-blue-300">
                        <GitBranch size={9} /> thread
                      </span>
                    )}
                    {r.duplicate && <span className="text-amber-400">dup</span>}
                    {(r.record.rawFilePaths?.length ?? 0) > 0 && (
                      <span
                        role="button"
                        tabIndex={0}
                        title="Open this record's original email"
                        onClick={(e) => {
                          e.stopPropagation();
                          const refs = r.record.rawFilePaths!;
                          setViewerRecordId(r.record.id);
                          setViewerSha((refs.find((f) => f.kind === 'email_source') ?? refs[0]).sha256);
                        }}
                        onKeyDown={(e) => {
                          if (e.key !== 'Enter' && e.key !== ' ') return;
                          e.preventDefault();
                          e.stopPropagation();
                          const refs = r.record.rawFilePaths!;
                          setViewerRecordId(r.record.id);
                          setViewerSha((refs.find((f) => f.kind === 'email_source') ?? refs[0]).sha256);
                        }}
                        className="ml-0.5 rounded p-0.5 text-stone-500 transition-colors hover:text-amber-300"
                      >
                        <Eye size={10} />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
          {isDuplicate && (
            <div className="flex items-start gap-2 px-3 py-2 rounded-lg border border-amber-400/25 bg-amber-400/[0.06] text-[11px] text-amber-300">
              <AlertTriangle size={13} className="mt-0.5 shrink-0" />
              <span>{duplicateNotice}</span>
              <button
                onClick={handleArchiveAndReparse}
                disabled={parsing}
                className="ml-auto shrink-0 px-2.5 py-1 rounded-lg border border-amber-400/40 text-amber-300 hover:bg-amber-400/10 text-[10px] font-medium uppercase tracking-wide transition-colors disabled:opacity-50"
              >
                {parsing ? 'Re-parsing…' : 'Archive & Re-parse'}
              </button>
            </div>
          )}
          {results.length > 1 && (
            <div className="px-3 py-2 rounded-lg border border-blue-400/20 bg-blue-400/[0.05] text-[11px] text-blue-300">
              {results.length} intake records created — click a record above to inspect it; thread
              replies show their linkage and proposed updates on their own record.
            </div>
          )}
          {documentGrouping && (
            <div className="panel">
              <div className="panel-header">
                <div className="flex items-center gap-1.5">
                  <GitBranch size={11} className="text-blue-400" />
                  <span className="panel-header-title">Document Grouping</span>
                </div>
                <span className="text-[9px] font-mono uppercase text-stone-600">
                  group {documentGrouping.group} of {documentGrouping.groupsInRequest} · {documentGrouping.mode}
                </span>
              </div>
              <div className="panel-body space-y-1.5 text-[11px]">
                <div className="flex gap-2">
                  <span className="text-stone-500 w-[70px] shrink-0">Files</span>
                  <span className="text-stone-200">{documentGrouping.files.join(', ')}</span>
                </div>
                <div className="flex gap-2">
                  <span className="text-stone-500 w-[70px] shrink-0">Basis</span>
                  <span className="text-stone-400">{documentGrouping.basis}</span>
                </div>
                {(documentGrouping.notes ?? []).map((note) => (
                  <div key={note} className="flex items-start gap-1.5 text-[10px] text-amber-300/90">
                    <AlertTriangle size={11} className="mt-0.5 shrink-0" />
                    <span>{note}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {liveEnvelope && (
            <div className="panel">
              <div className="panel-header">
                <div className="flex items-center gap-1.5">
                  <Mail size={11} className="text-blue-400" />
                  <span className="panel-header-title">Email Envelope</span>
                </div>
                <span className="text-[9px] font-mono uppercase text-stone-600">.{liveEnvelope.container}</span>
              </div>
              <div className="panel-body space-y-1 text-[11px]">
                <div className="flex gap-2"><span className="text-stone-500 w-[70px] shrink-0">From</span>
                  <span className="text-stone-200">{liveEnvelope.fromName ?? ''} {liveEnvelope.fromEmail ? `<${liveEnvelope.fromEmail}>` : '—'}</span></div>
                <div className="flex gap-2"><span className="text-stone-500 w-[70px] shrink-0">Subject</span>
                  <span className="text-stone-200">{liveEnvelope.subject ?? '—'}</span></div>
                <div className="flex gap-2"><span className="text-stone-500 w-[70px] shrink-0">Sent</span>
                  <span className="text-stone-200">{formatDeskDateTime(liveEnvelope.sentAt) || '—'}</span></div>
                <div className="flex gap-2"><span className="text-stone-500 w-[70px] shrink-0">Body</span>
                  <span className="text-stone-400 font-mono text-[10px]">{liveEnvelope.bodySource}</span></div>
              </div>
            </div>
          )}
          {/* Always offer the source viewer whenever files were persisted —
              not only when an email envelope panel rendered. Standalone PDF /
              Excel drops and envelopes that omitted body metadata were
              previously invisible here. */}
          {(result?.rawFilePaths?.length ?? 0) > 0 && (
            <button
              type="button"
              onClick={() => {
                setViewerRecordId(result!.id);
                setViewerSha(
                  result!.rawFilePaths!.find((f) => f.kind === 'email_source')?.sha256
                  ?? result!.rawFilePaths![0].sha256,
                );
              }}
              className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-amber-400/30 bg-amber-400/[0.06] px-3 py-2 text-[11px] text-amber-300 hover:bg-amber-400/10 transition-colors"
            >
              <Eye size={12} />
              Open original email &amp; attachments — verify against the source
            </button>
          )}
          <div className="panel">
            <div className="panel-header">
              <div className="flex items-center gap-1.5">
                <Sparkles size={11} className="text-amber-400" />
                <span className="panel-header-title">Extracted Fields</span>
              </div>
              <span
                className="text-[10px] text-emerald-400"
                title={demo ? undefined : 'Deterministic completeness: found core fields ÷ 8 core fields. Not a probabilistic confidence.'}
              >
                {coverage}% {demo ? 'confidence' : 'field coverage'}
              </span>
            </div>
            <div className="panel-body grid grid-cols-2 gap-x-6 gap-y-1.5">
              {rows.map((row, i) => (
                <div key={i} className="flex items-center gap-2 text-[11px]">
                  <span className="text-stone-500 w-[100px] shrink-0">{row.label}</span>
                  <span className="text-stone-200 font-medium">{row.value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Traceability panel (Item 3) */}
          <div className="panel border-blue-400/20">
            <div className="panel-header">
              <div className="flex items-center gap-1.5">
                <Link2 size={11} className="text-blue-400" />
                <span className="panel-header-title text-blue-400">Source Traceability</span>
              </div>
            </div>
            <div className="panel-body">
              <p className="text-[10px] text-stone-500 mb-2">
                All extracted fields are traced back to their source document and cell reference for full auditability.
              </p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                {tracedFields.length === 0 ? (
                  <p className="col-span-2 text-[10px] text-stone-600">No cell-level traceability captured for this file type.</p>
                ) : (
                  tracedFields.map((tf, i) => {
                    const srcName = tf.split('←').pop()?.trim() ?? '';
                    const match = (result?.rawFilePaths ?? []).find(
                      (f) => f.filename === srcName
                        || srcName.endsWith(f.filename)
                        || f.filename.endsWith(srcName),
                    );
                    return (
                    <div key={i} className="flex min-w-0 items-center gap-2 text-[10px] text-stone-400">
                      <Braces size={9} className="shrink-0 text-blue-400" />
                      {match ? (
                        <button
                          type="button"
                          onClick={() => {
                            setViewerRecordId(result!.id);
                            setViewerSha(match.sha256);
                          }}
                          className="min-w-0 truncate text-left font-mono text-blue-300 hover:text-amber-300"
                          title="Open this source file"
                        >
                          {tf}
                        </button>
                      ) : (
                        <span className="min-w-0 truncate font-mono text-blue-300" title={tf}>{tf}</span>
                      )}
                      {demo && <span className="shrink-0 text-stone-600">← {sourceFile}</span>}
                    </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>

          {/* The dossier filling in, and what still blocks processing. Live
              records only — a demo preview creates no record to read. */}
          {!demo && result?.id && <DossierCoveragePanel ingestionId={result.id} />}

          {liveReport && <ConflictsPanel conflicts={liveReport.conflicts ?? []} />}
          {liveReport && <GuidancePanel guidance={liveReport.guidanceFields ?? []} />}
          {liveReport?.thread && result && (
            <ThreadPanel
              key={result.id}
              ingestionId={result.id}
              thread={liveReport.thread}
              onApplied={() => queryClient.invalidateQueries({ queryKey: ['ingestion-queue'] })}
            />
          )}
          {liveReport?.attachments && (
            <AttachmentReportList
              attachments={liveReport.attachments}
              ingestionId={result?.id}
              onOpenSource={(sha) => {
                if (!result) return;
                setViewerRecordId(result.id);
                setViewerSha(sha);
              }}
            />
          )}
          {liveScreens && <ScreenResultsPanel screens={liveScreens} />}
          {liveReport && (liveReport.unmatchedLines?.length ?? 0) > 0 && (
            <div className="panel">
              <div className="panel-header">
                <span className="panel-header-title">Not machine-read — for your review</span>
                <span className="text-[10px] text-stone-600">
                  {(liveReport.unmatchedLines?.length ?? 0) + (liveReport.unmatchedTruncated ?? 0)} line(s)
                </span>
              </div>
              <div className="panel-body space-y-1">
                {(liveReport.unmatchedLines ?? []).slice(0, 6).map((u, i) => (
                  <p key={i} className="text-[10px] text-stone-500 font-mono truncate" title={u.reason}>
                    {u.text}
                  </p>
                ))}
                <p className="text-[9px] text-stone-600">
                  Labeled lines the grammar recognized but could not parse safely. Nothing here was guessed at.
                </p>
              </div>
            </div>
          )}

          <div className="flex gap-2">
            <button
              onClick={() => handleCreateDeal()}
              disabled={converting}
              className="btn-primary flex-1 flex items-center justify-center gap-2 text-[11px] py-2 disabled:opacity-50"
            >
              {converting ? (
                <>
                  <div className="w-3 h-3 border-2 border-amber-900/30 border-t-amber-900 rounded-full animate-spin" />
                  Creating deal…
                </>
              ) : (
                <>
                  <ArrowRight size={12} />
                  {demo ? 'Preview Pipeline — Not Saved' : 'Create Deal Record'}
                </>
              )}
            </button>
            <button onClick={reset} className="btn-secondary flex items-center gap-2 text-[11px] px-3 py-2">
              <Upload size={11} />
              Upload another
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="flex items-start gap-2 px-3 py-2 rounded-lg border border-red-500/30 bg-red-500/10 text-[11px] text-red-300">
          <AlertTriangle size={13} className="mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}
      {threadBlocked && (
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleCreateDeal(true)}
            disabled={converting}
            className="btn-secondary text-[10px] px-3 py-1.5 disabled:opacity-50"
          >
            This is a separate deal — create a new loan anyway
          </button>
          <span className="text-[9px] text-stone-600">
            Otherwise, apply this message's updates to the existing loan in the panel above.
          </span>
        </div>
      )}

      {/* The viewer follows whichever record was clicked — a queued file or a
          thread chip — falling back to the active record for the envelope
          link. Keying it to the active record only meant clicking record 2
          silently showed record 1's email. */}
      {(() => {
        const target = results.find((r) => r.record.id === viewerRecordId)?.record ?? result;
        if (!viewerSha || !target || (target.rawFilePaths?.length ?? 0) === 0) return null;
        return (
          <SourceViewerDialog
            ingestionId={target.id}
            files={target.rawFilePaths!}
            initialSha={viewerSha}
            onClose={() => { setViewerSha(null); setViewerRecordId(null); }}
          />
        );
      })()}

      {confirmNew && result && (
        <ConfirmNewDealDialog
          confirmation={confirmNew}
          busy={converting}
          error={confirmError}
          onConfirm={(name, overrides) => void confirmUploadName(name, overrides)}
          onClose={() => setConfirmNew(null)}
          onOpenDeal={(loanId) => openDossier(loanId)}
        />
      )}

      <WhatHappensNext />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

/** One-line health strip for the originations mailbox poller. Reads the
 *  status endpoint only — nothing here mutates state. Red when the poller is
 *  disabled or erroring, amber when the mailbox holds messages the ledger
 *  has not yet resolved, otherwise a quiet "last checked" line. */
function InboundMailStatusStrip({ status }: { status: InboundMailStatus }) {
  const inbox = status.mailboxSnapshot?.folders?.inbox;
  const junk = status.mailboxSnapshot?.folders?.junkemail;
  const totals = status.ledgerTotals || {};
  const ingested = totals.ingested ?? 0;
  const duplicate = totals.duplicate ?? 0;
  const rejected = totals.rejected ?? 0;
  const failed = totals.failed ?? 0;
  const lastTick = status.poller?.lastPollFinishedAt;
  const lastTickLabel = lastTick
    ? `${Math.max(0, Math.round((Date.now() - new Date(lastTick).getTime()) / 1000))}s ago`
    : 'never';
  const problem = !status.enabled
    ? 'Mailbox poller is disabled — Graph credentials, inbound token, or mailbox unset.'
    : status.poller?.lastError
      ? `Last poll failed: ${status.poller.lastError}`
      : null;
  const tone = problem ? 'text-red-300' : 'text-stone-500';
  return (
    <div className={`mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 font-mono ${tone}`} title={problem || undefined}>
      <span>
        {status.enabled ? 'poller' : 'poller OFF'}
        {status.enabled && <span className="text-stone-600"> · checked {lastTickLabel}</span>}
      </span>
      {inbox && !('error' in inbox) && (
        <span className="text-stone-600">
          inbox {inbox.total ?? '—'}{junk && !('error' in junk) ? ` · junk ${junk.total ?? '—'}` : ''}
        </span>
      )}
      <span className="text-stone-600">
        ingested {ingested}
        {duplicate ? ` · dup ${duplicate}` : ''}
        {rejected ? <span className="text-amber-300"> · rejected {rejected}</span> : null}
        {failed ? <span className="text-red-300"> · failed {failed}</span> : null}
      </span>
      {problem && <span className="basis-full truncate">{problem}</span>}
    </div>
  );
}

export default function Intake() {
  const { isModuleDemoEnabled } = useDemoDataSettings();
  const demo = isModuleDemoEnabled('intake');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const recentDeals = intakeDemoDeals(demo);

  // When demo is off, the queue is driven by real ingestion records from the API.
  const { data: liveQueue } = useQuery({
    queryKey: ['ingestion-queue'],
    queryFn: ingestionService.getQueue,
    enabled: !demo,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });
  const requests: IntakeRequest[] = demo ? INTAKE_REQUESTS : (liveQueue ?? []).map(mapIngestionToRequest);

  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedId, setSelectedId] = useState<string>('');
  // Which analytic is driving the view, and therefore which records are shown.
  // The tile carries its own ids, so the filter is the server's answer rather
  // than a second predicate that has to stay in step with it.
  const [analytic, setAnalytic] = useState<IntakeAnalytic | null>(null);
  const [hoverSha, setHoverSha] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<IntakeTab>('queue');

  const { data: inboundStatus } = useQuery({
    queryKey: ['inbound-mail-status'],
    queryFn: () => ingestionService.getInboundMailStatus(20),
    enabled: !demo && activeTab === 'emailed',
    refetchInterval: 30_000,
  });

  // Deep link from global search: /intake?focus=INQ-… opens that record.
  // `&review=1` opens its Data Intelligence Review too — the
  // record may be long since a deal, which is exactly when the desk needs it.
  //
  // The parameter stays in the URL until the record it names is on screen
  // (see the effect after `selected`). Removing it at once lost the link
  // whenever the page loaded twice — a first visit reloads once to settle the
  // demo setting — and the page then opened whatever record came first
  // (stress test REG-06: a deep link to Cranbury showed 45 Park).
  const focusId = searchParams.get('focus');
  const reviewRequested = searchParams.get('review') === '1';
  const [openReviewFor, setOpenReviewFor] = useState<string | null>(null);
  useEffect(() => {
    if (!focusId) return;
    setActiveTab('queue');
    setSelectedId(focusId);
    if (reviewRequested) setOpenReviewFor(focusId);
  }, [focusId, reviewRequested]);
  // `/intake?review=1` with no record named opens the desk-wide Data Intelligence Review: the link the deal
  // book import leaves for the deals it opened there. With a record named, the effect above opens that record's.
  useEffect(() => {
    if (focusId || !reviewRequested) return;
    setActiveTab('review');
    const next = new URLSearchParams(searchParams);
    next.delete('review');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusId, reviewRequested]);
  const [actioning, setActioning] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [threadBlocked, setThreadBlocked] = useState(false);
  const [viewerSha, setViewerSha] = useState<string | null>(null);
  const [viewerAnchor, setViewerAnchor] = useState<SourceAnchor | null>(null);
  /** Extra file refs (e.g. attachment sha not yet listed on rawFilePaths). */
  const [viewerFilesOverride, setViewerFilesOverride] = useState<SourceFileRef[] | null>(null);
  const [viewerUnavailableReason, setViewerUnavailableReason] = useState<string | null>(null);
  const [dealFileId, setDealFileId] = useState<string | null>(null);
  const dealFileOnSelect = useRef<string | null>(null);
  // The Confirmation view, opened on the deal this record became.
  const [confirmDealId, setConfirmDealId] = useState<string | null>(null);
  // F6: which record the internal info request is being composed for.
  const [infoRequestId, setInfoRequestId] = useState<string | null>(null);
  // Re-run: measure the current engine against this record's stored originals.
  const [rerunId, setRerunId] = useState<string | null>(null);
  // What the Data Intelligence Review tab badges: every open question on the
  // desk plus the found datapoints. The tab itself reads the same query, so
  // opening it does not fetch the desk a second time — and it reads it with the
  // inbox's view: when the inbox lists the unbacked found datapoints or a longer
  // page, the badge takes its totals (the desk's either way) from that answer
  // instead of keeping a second read of the desk alive beside it.
  const inboxLimit = useDataReviewInboxView((view) => view.limit);
  const inboxUnbacked = useDataReviewInboxView((view) => view.includeUnbacked);
  const reviewInbox = useQuery({
    ...inboxQuery({ limit: inboxLimit, includeUnbacked: inboxUnbacked }),
    enabled: !demo,
    retry: false,
    // The count stays on the tab while a longer or fuller answer is read.
    placeholderData: keepPreviousData,
  });
  const reviewCount = reviewInbox.data?.totals.all ?? 0;
  // The Data Intelligence Review, opened on the selected record — at the top, or
  // on a row the re-parse notice named.
  const [dataReviewOpen, setDataReviewOpen] = useState(false);
  const [dataReviewStart, setDataReviewStart] = useState<string | null>(null);
  // Pre-commit duplicate preview (register A8): populated when the operator asks
  // to create a deal and the record may be a re-upload of an existing one.
  const [matchPreview, setMatchPreview] = useState<{ candidates: MatchCandidate[]; note?: string } | null>(null);
  const [confirmNew, setConfirmNew] = useState<
    (NewDealConfirmation & { forceNewLoan: boolean }) | null
  >(null);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [communicationSha, setCommunicationSha] = useState<string | null>(null);
  const prevTabRef = useRef(activeTab);

  const emailedRequests = requests.filter((r) => r.sourceType === 'email');
  const byTab = activeTab === 'emailed' ? emailedRequests : requests;
  // A selected analytic shows the exact records it counted, FETCHED BY ID.
  // Filtering `byTab` instead looked equivalent and was not: the queue returns
  // only `new` and `reviewed`, so a tile counting converted records opened an
  // empty list under its own figure.
  const { data: analyticRows } = useQuery({
    queryKey: ['ingestion-by-ids', analytic?.key, analytic?.ids],
    queryFn: () => ingestionService.listByIds(analytic?.ids ?? []),
    enabled: !demo && !!analytic,
  });
  const visibleRequests = analytic
    ? (analyticRows ?? []).map(mapIngestionToRequest)
    : byTab;
  // A record the operator ASKED for by id must never resolve to a different one.
  //
  // The queue endpoint returns only `new` and `reviewed`, while Emailed Intake
  // lists every email including converted ones. Clicking a converted row — or
  // following /intake?focus=INQ-… from global search — set an id the queue array
  // could not contain, and this line fell through to `visibleRequests[0]`, so the
  // screen silently opened an unrelated deal. `getById` has existed on both sides
  // all along and nothing here called it.
  const inList = visibleRequests.find((r) => r.id === selectedId);
  const { data: hydratedRecord } = useQuery({
    queryKey: ['ingestion-record', selectedId],
    queryFn: () => ingestionService.getById(selectedId),
    enabled: !demo && !!selectedId && !inList,
    retry: false,
  });
  const selected = resolveSelectedRequest(
    visibleRequests,
    selectedId,
    hydratedRecord ? mapIngestionToRequest(hydratedRecord) : undefined,
  );
  const disposition = recordDisposition(selected ?? {});

  // The deep-linked record is on screen: the link has done its job.
  useEffect(() => {
    if (!focusId || selected?.id !== focusId) return;
    const next = new URLSearchParams(searchParams);
    next.delete('focus');
    next.delete('review');
    setSearchParams(next, { replace: true });
  }, [focusId, selected?.id, searchParams, setSearchParams]);

  useEffect(() => {
    if (activeTab === 'emailed' && prevTabRef.current !== 'emailed') {
      setSelectedId('');
    }
    prevTabRef.current = activeTab;
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== 'emailed') return;
    if (selectedId && !emailedRequests.some((r) => r.id === selectedId)) {
      setSelectedId('');
    }
  }, [activeTab, emailedRequests, selectedId]);

  useEffect(() => {
    if (activeTab !== 'emailed' || !selected) {
      setCommunicationSha(null);
      return;
    }
    const emailFile = selected.rawFilePaths?.find((f) => f.kind === 'email_source');
    setCommunicationSha(emailFile?.sha256 ?? null);
  }, [activeTab, selected?.id, selected?.rawFilePaths]);

  useEffect(() => {
    setActionError(null);
    setThreadBlocked(false);
    setViewerSha(null);
    setViewerAnchor(null);
    setViewerFilesOverride(null);
    setViewerUnavailableReason(null);
    // A deal file asked for with the selection (a double-click on a queue card)
    // opens over the record it was asked from; anything else closes.
    setDealFileId(dealFileOnSelect.current);
    dealFileOnSelect.current = null;
    setDataReviewOpen(false);
    setDataReviewStart(null);
    setMatchPreview(null);
    setHoverSha(null);
  }, [selectedId]);

  // R-13 (29 Oct 2026): a double-click on a queue card opens what the card is.
  // A record that became a deal opens that deal's file, as its Open deal file
  // button does. A record that is not a deal yet is the record: it is selected
  // and the detail pane takes the focus. The demo book's deal ids name no deal.
  const openQueueRecord = (req: IntakeRequest) => {
    const dealId = req.createdDealId && !demo ? req.createdDealId : null;
    if (req.id !== selectedId) {
      dealFileOnSelect.current = dealId;
      setSelectedId(req.id);
    } else if (dealId) {
      setDealFileId(dealId);
    }
    if (!dealId) {
      window.requestAnimationFrame(() => document.getElementById('intake-request-detail')?.focus());
    }
  };

  // Declared after the reset above so, on the render that lands the deep-linked
  // record, the reset runs first and this opens the Data Intelligence Review
  // over it.
  useEffect(() => {
    if (!openReviewFor || demo || selected?.id !== openReviewFor) return;
    setDataReviewOpen(true);
    setOpenReviewFor(null);
  }, [openReviewFor, selected?.id, demo]);

  // WP-J click-to-source: resolve a field's source (email or attachment name)
  // to its stored file and open the viewer scrolled + highlighted there.
  const openSource = (source: string, anchor: SourceAnchor | null) => {
    const refs = selected?.rawFilePaths ?? [];
    const target = source.startsWith('email_')
      || source === 'email_body (quoted)' || source.startsWith('email_body')
      ? refs.find((f) => f.kind === 'email_source')
      : refs.find((f) => f.filename === source) ?? refs.find((f) => source.includes(f.filename));
    if (!target) return;
    setViewerUnavailableReason(null);
    setViewerFilesOverride(null);
    setViewerAnchor(anchor);
    setViewerSha(target.sha256);
  };

  const openAttachment = (att: {
    filename: string;
    type?: string;
    sizeKb?: number;
    parsed?: boolean;
    sha256?: string;
  }) => {
    const match = resolveAttachmentSource(att, selected?.rawFilePaths);
    if (match) {
      const base = selected?.rawFilePaths ?? [];
      const needsOverride = !base.some((f) => f.sha256 === match.sha256);
      setViewerUnavailableReason(null);
      setViewerFilesOverride(needsOverride ? [...base, match] : null);
      setViewerAnchor(null);
      setViewerSha(match.sha256);
      return;
    }
    // No stored binary (demo fixtures, or metadata-only rows) — still open the
    // viewer shell so the click lands in a popup rather than doing nothing.
    const placeholder: SourceFileRef = {
      filename: att.filename,
      kind: 'attachment',
      sha256: `unavailable:${att.filename}`,
    };
    setViewerFilesOverride([placeholder]);
    setViewerUnavailableReason(
      demo
        ? 'Demo fixtures have no stored originals. Turn demo data off and upload a real email to open the attachment here.'
        : 'This record has no stored original for this attachment. Re-upload the source package on Intake to restore it.',
    );
    setViewerAnchor(null);
    setViewerSha(placeholder.sha256);
  };

  const closeSourceViewer = () => {
    setViewerSha(null);
    setViewerAnchor(null);
    setViewerFilesOverride(null);
    setViewerUnavailableReason(null);
  };

  const openStoredSource = (sha: string, anchor: SourceAnchor | null = null) => {
    setViewerUnavailableReason(null);
    setViewerFilesOverride(null);
    setViewerAnchor(anchor);
    setViewerSha(sha);
  };

  const viewerFiles =
    viewerFilesOverride ?? selected?.rawFilePaths ?? [];

  // The record's dossier projection — every catalogue field with its value,
  // status and provenance. The required-fields panel is a view onto it, so the
  // panel and the coverage bar below can never disagree about what was found.
  const requiredCoverage = useQuery({
    queryKey: ['ingestion-dossier', selected?.id],
    queryFn: () => ingestionService.getDossier(selected!.id),
    enabled: Boolean(selected?.id) && !demo,
    staleTime: 30_000,
  });

  const doConvert = async (forceNewLoan = false, overrides?: ConvertPayload['overrides']) => {
    if (!selected || demo) return;
    setMatchPreview(null);
    setActioning(true);
    setActionError(null);
    setThreadBlocked(false);
    try {
      // A person creating the deal: held to the loan purpose, city + state,
      // initial and fully funded amounts (client item #1). Automatic creation
      // (off by default, an administrator's switch) never is.
      const result = await ingestionService.convert(selected.id, {
        priority: selected.aiAssessment.priority,
        forceNewLoan,
        requireIntakeFields: true,
        overrides,
      });
      queryClient.invalidateQueries({ queryKey: ['ingestion-queue'] });
      // Previously this navigated to /pipeline with no deal id, and that page
      // selects the first loan in the list — so converting dropped the operator
      // on an unrelated record with the one they just created nowhere in sight.
      // Open the deal that was actually created, without leaving intake.
      if (result?.loan?.id) setDealFileId(result.loan.id);
      else navigate('/pipeline');
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
      // The server refuses a name nobody confirmed when it has a better one
      // to suggest, or a killed deal is at stake (nd-037 / nd3-19).
      const refusal = confirmationFromRefusal(detail);
      if (refusal) {
        setConfirmError(null);
        setConfirmNew({ ...refusal, forceNewLoan });
        return;
      }
      // The deal cannot be created until the four fields required at intake are
      // there: the dialog asks for each one the server named.
      const gaps = requiredGapsFromRefusal(detail);
      if (gaps) {
        setConfirmError(null);
        setConfirmNew((prev) => ({
          needsName: false, suggested: null, subjectName: null, killed: [],
          ...(prev ?? {}), missing: gaps, forceNewLoan,
        }));
        return;
      }
      if (typeof detail === 'string' && detail.includes('force_new_loan')) {
        setThreadBlocked(true);
      }
      setActionError(typeof detail === 'string' ? detail : 'Could not create the deal record. Please retry.');
    } finally {
      setActioning(false);
    }
  };

  // Create Deal on a record held for its name, or matching a killed deal,
  // asks first: the name the hold waits on is confirmed as the person's
  // decision (resolve-name, which creates the deal), and the killed deal is
  // shown before a new one is made (nd-037 / nd3-19). Either way the deal is
  // held to the four fields required at intake; the answers to any still
  // missing ride along as the person's own.
  const confirmNewDeal = async (name: string | null, overrides?: ConvertPayload['overrides']) => {
    if (!selected || demo || !confirmNew) return;
    const { forceNewLoan } = confirmNew;
    if (name === null) {
      setConfirmNew(null);
      await proceedToConvert(forceNewLoan, overrides);
      return;
    }
    setActioning(true);
    setConfirmError(null);
    try {
      const record = await ingestionService.resolveDealName(selected.id, name, undefined, {
        requireIntakeFields: true,
        overrides,
      });
      setConfirmNew(null);
      queryClient.invalidateQueries({ queryKey: ['ingestion-queue'] });
      queryClient.invalidateQueries({ queryKey: ['ingestion-dossier', selected.id] });
      queryClient.invalidateQueries({ queryKey: ['ingestion-record', selected.id] });
      if (record?.convertedToLoanId) {
        setDealFileId(record.convertedToLoanId);
      } else if (forceNewLoan) {
        await doConvert(true, overrides);
      }
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
      const gaps = requiredGapsFromRefusal(detail);
      if (gaps) {
        // Nothing was recorded: the dialog stays open and asks for what is missing.
        setConfirmNew((prev) => (prev ? { ...prev, missing: gaps } : prev));
        return;
      }
      setConfirmError(typeof detail === 'string' ? detail : 'Could not record the name. Please retry.');
    } finally {
      setActioning(false);
    }
  };

  // Committing a record is gated by the duplicate preview: if this record may be
  // a re-upload of an existing deal, the operator decides attach vs create new
  // BEFORE anything is committed. No candidates → straight through to convert.
  const convertSelected = async () => {
    if (!selected || demo) return;
    const confirmation = newDealConfirmation(selected, requiredCoverage.data);
    if (confirmation) {
      setActionError(null);
      setConfirmError(null);
      setConfirmNew({ ...confirmation, forceNewLoan: false });
      return;
    }
    await proceedToConvert(false);
  };

  const proceedToConvert = async (forceNewLoan: boolean, overrides?: ConvertPayload['overrides']) => {
    if (!selected || demo) return;
    // With answers in hand the duplicate preview has already been settled (the
    // person is answering the server's refusal), so it is not shown again.
    if (forceNewLoan || overrides) {
      await doConvert(forceNewLoan, overrides);
      return;
    }
    setActioning(true);
    setActionError(null);
    setThreadBlocked(false);
    try {
      const preview = await ingestionService.getMatchPreview(selected.id);
      if (preview.candidates.length > 0) {
        setMatchPreview({ candidates: preview.candidates, note: preview.note });
        setActioning(false);
        return;
      }
    } catch {
      // The preview is advisory. If it cannot be fetched, do not block the
      // commit — fall through to the normal convert path.
    }
    await doConvert(false);
  };

  // "Attach to existing" — link this record to a deal that already exists rather
  // than creating a duplicate. The ingestion is marked converted and pointed at
  // the chosen loan; no new deal is created (register A8).
  const attachToExisting = async (loanId: string) => {
    if (!selected || demo) return;
    setActioning(true);
    setActionError(null);
    try {
      await ingestionService.review(selected.id, {
        status: 'converted',
        convertedToLoanId: loanId,
      });
      setMatchPreview(null);
      queryClient.invalidateQueries({ queryKey: ['ingestion-queue'] });
      setDealFileId(loanId);
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setActionError(typeof detail === 'string' ? detail : 'Could not attach to the existing deal. Please retry.');
    } finally {
      setActioning(false);
    }
  };

  const declineSelected = async () => {
    if (!selected || demo) return;
    setActioning(true);
    try {
      await ingestionService.review(selected.id, { status: 'declined' });
      queryClient.invalidateQueries({ queryKey: ['ingestion-queue'] });
      setSelectedId('');
    } finally {
      setActioning(false);
    }
  };

  const archiveSelected = async () => {
    if (!selected || demo) return;
    const confirmed = window.confirm(
      `Archive ${selected.id}? It leaves the queue (recoverable by ID for audit) and its ` +
      'content hash is released, so the same file can be re-uploaded for a fresh parse.',
    );
    if (!confirmed) return;
    setActioning(true);
    try {
      await ingestionService.archive(selected.id);
      queryClient.invalidateQueries({ queryKey: ['ingestion-queue'] });
      setSelectedId('');
    } finally {
      setActioning(false);
    }
  };

  return (
    <div className="page-fill gap-3 px-4 py-3">
      {/* ── Intake analytics (register P1) ──
          One number, and every figure below it a subset with a stated basis.
          Selecting a figure filters the lists to the exact records the server
          counted, so a reviewer can always see where a number came from.

          This replaced a strip whose values came from `intakeDemoMetrics()`,
          which returns literal zeros and a hard-coded 4.2 on any LIVE account.
          A fabricated metric on a live screen is worse than no metric. ── */}
      <IntakeAnalyticsStrip
        demo={demo}
        activeKey={analytic?.key ?? null}
        onSelect={(next) => {
          setAnalytic(next);
          setSelectedId('');
          if (next) setActiveTab(next.tab);
        }}
        trailing={
          <div className="flex items-stretch gap-2">
            {/* Every record, promoted or not: a promoted record is the one the
                desk is working, and its review items must be reachable
                (stress test REG-06). Decisions carry through to the deal. */}
            {selected ? (
              <SmartReviewSummaryCard
                counts={smartReviewSummaryCounts(selected.parseReport)}
                onOpen={() => {
                  // A revision's only open items are the values it contests,
                  // and they are decided in the Email Thread panel.
                  if (disposition.kind === 'revision_pending' || disposition.kind === 'revision_on_killed_deal') {
                    document.getElementById('intake-thread-panel')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    return;
                  }
                  setDataReviewOpen(true);
                }}
                disabled={demo}
                className="h-full max-w-[260px]"
              />
            ) : null}
          </div>
        }
      />

      {/* ── Tab switcher ── */}
      <div className="flex flex-shrink-0 items-center gap-1 border-b border-amber-400/10">
        {INTAKE_TABS.map(([tab, label]) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`relative px-4 py-2 text-[11px] font-medium uppercase tracking-wide transition-colors ${
              activeTab === tab
                ? 'text-amber-300'
                : 'text-stone-500 hover:text-stone-300'
            }`}
          >
            {label}
            {tab === 'review' && reviewCount > 0 && reviewInbox.data && (
              <span
                role="img"
                className="ml-1.5 rounded-full bg-amber-400/20 px-1.5 py-px text-[9px] font-semibold tabular-nums text-amber-200"
                aria-label={describeDesk(reviewInbox.data.totals)}
                title={describeDesk(reviewInbox.data.totals)}
              >
                {reviewCount}
              </span>
            )}
            {activeTab === tab && (
              <span className="absolute bottom-0 left-3 right-3 h-px bg-amber-400" />
            )}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-3 text-[10px] text-stone-600">
          {/* Present only while the manager step is on and this account can
              approve; draws nothing otherwise. */}
          {!demo && <ManagerQueueButton />}
          <span className="flex items-center gap-1.5">
            <Ban size={10} className="text-amber-400" />
            <span className="text-amber-400">Inbound-only automation</span>
          </span>
        </div>
      </div>

      {activeTab === 'review' ? (
        <DataReviewInbox />
      ) : activeTab === 'manual' ? (
        <div className="grid min-h-0 flex-1 grid-cols-[1fr_340px] gap-3 overflow-y-auto scrollbar-thin">
          <ManualDealEntry />
          <div className="panel">
            <div className="panel-header">
              <span className="panel-header-title">Recent Deals from Intake</span>
            </div>
            <div className="panel-body space-y-1.5">
              {recentDeals.map((d, i) => (
                <div key={i} className="flex items-center justify-between py-1 border-b border-amber-400/5 last:border-0">
                  <div className="flex items-center gap-2 min-w-0">
                    <Building2 size={11} className="text-stone-600 shrink-0" />
                    <span className="text-[11px] text-stone-300 truncate">{d.property}</span>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="text-[11px] font-mono text-stone-400">${d.amount.toFixed(1)}M</span>
                    {stageBadge(d.stage)}
                  </div>
                </div>
              ))}
              {recentDeals.length === 0 && (
                <p className="text-[10px] text-stone-600">Deals you log will appear on the Pipeline and daily report.</p>
              )}
            </div>
          </div>
        </div>
      ) : activeTab === 'upload' ? (
        <div className="grid min-h-0 flex-1 grid-cols-[1fr_340px] gap-3 overflow-y-auto scrollbar-thin">
          <UploadPanel demo={demo} />
          {/* Recent Deals sidebar */}
          <div className="panel">
            <div className="panel-header">
              <span className="panel-header-title">Recent Deals from Intake</span>
            </div>
            <div className="panel-body space-y-1.5">
              {recentDeals.map((d, i) => (
                <div key={i} className="flex items-center justify-between py-1 border-b border-amber-400/5 last:border-0">
                  <div className="flex items-center gap-2 min-w-0">
                    <Building2 size={11} className="text-stone-600 shrink-0" />
                    <span className="text-[11px] text-stone-300 truncate">{d.property}</span>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="text-[11px] font-mono text-stone-400">${d.amount.toFixed(1)}M</span>
                    {stageBadge(d.stage)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : activeTab === 'emailed' && !demo ? (
        /* ── Emailed Intake: the originations mailbox as a triage table ── */
        <div className="flex min-h-0 flex-1 flex-col gap-2">
          {inboundStatus && (
            <div className="flex-shrink-0 rounded-lg border border-amber-400/10 bg-black/20 px-3 py-1.5 text-[10px] text-stone-500">
              <p>
                Messages delivered to{' '}
                <span className="font-mono text-amber-300">
                  {inboundStatus.mailbox || 'origination@helios-fintech.com'}
                </span>
              </p>
              <InboundMailStatusStrip status={inboundStatus} />
            </div>
          )}
          <EmailedIntakeTable
            onOpenRecord={(id) => {
              setActiveTab('queue');
              setSelectedId(id);
            }}
            // A selected strip figure filters this list too, to the exact
            // records it counted — 'Supporting mail 1' opened on every email.
            focus={analytic}
            onClearFocus={() => setAnalytic(null)}
          />
        </div>
      ) : (
        /* ── Queue view (demo emailed falls through here) ── */
        <div className="grid min-h-0 flex-1 grid-cols-[280px_minmax(0,1fr)] gap-3">
          <div className="flex min-h-0 flex-col gap-3">
            <div className="panel flex min-h-0 flex-1 flex-col overflow-hidden">
              <div className="panel-header flex-shrink-0">
                <span className="panel-header-title">Incoming Requests</span>
                <span className="text-[10px] font-mono text-stone-600">{visibleRequests.length} total</span>
              </div>
              <div className="panel-body min-h-0 flex-1 overflow-y-auto space-y-2 scrollbar-thin">
                {visibleRequests.map((req) => {
                  // A revision to a deal already on the pipeline is not a new
                  // request, and its card must not read like one (LPR-24).
                  const revisionCard = queueCardSummary(req);
                  return (
                  <button
                    key={req.id}
                    data-testid="intake-queue-card"
                    onClick={() => setSelectedId(req.id)}
                    // R-13: a double-click opens the deal file the record became,
                    // as its Open deal file button does; a record that is not a
                    // deal yet is the record itself, in the detail pane.
                    onDoubleClick={() => openQueueRecord(req)}
                    title={req.createdDealId && !demo
                      ? `Double-click to open deal ${req.createdDealId}`
                      : 'Double-click to open the record'}
                    className={`w-full text-left rounded-lg border p-2.5 transition-all duration-150 ${
                      selectedId === req.id
                        ? 'border-amber-400/30 bg-amber-400/[0.06]'
                        : 'border-amber-400/10 bg-black/20 hover:border-amber-400/20 hover:bg-black/30'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      {statusDot(req.status)}
                      <span className="text-[10px] font-mono text-stone-500 uppercase">
                        {revisionCard ? revisionCard.statusLabel :
                          req.status === 'new' ? 'New' : req.status === 'reviewed' ? 'Reviewed' :
                          req.status === 'converted' ? 'Converted' : 'Declined'}
                      </span>
                      {sourceIcon(req.sourceType)}
                      <span className="text-[10px] text-stone-600 ml-auto">{req.relativeTime}</span>
                    </div>
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-[11px] font-medium text-amber-300">{req.brokerFirm}</span>
                      <span className="text-[10px] text-stone-500">|</span>
                      <span className="text-[11px] text-stone-400">{req.propertyType}</span>
                    </div>
                    <div className="flex items-start gap-1.5">
                      <p className="text-[11px] text-stone-300 leading-snug">{req.briefDescription}</p>
                      {req.parseReport?.dealName?.lowConfidence ? (
                        <span
                          className="mt-px shrink-0 rounded border border-amber-500/30 bg-amber-500/10 px-1 py-0.5 text-[8px] font-medium uppercase tracking-wide text-amber-300"
                          title="Deal name derived from the email subject — confirm the property name"
                        >
                          Low confidence
                        </span>
                      ) : null}
                    </div>
                    <div className="flex items-center justify-between mt-1.5">
                      {revisionCard ? (
                        <span className="text-[11px] font-mono text-amber-300">{revisionCard.line}</span>
                      ) : (
                        <span className="text-[11px] font-mono text-stone-200">{req.amount} financing req.</span>
                      )}
                      {req.attachments?.length ? (
                        <span className="flex items-center gap-1 text-[9px] text-stone-600">
                          <Paperclip size={8} /> {req.attachments.length} file{req.attachments.length > 1 ? 's' : ''}
                        </span>
                      ) : null}
                    </div>
                  </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Request Detail — queue tab always; emailed tab only when a row is selected */}
          {!selected ? (
            activeTab === 'emailed' ? null : (
            <div className="panel flex min-h-0 flex-col overflow-hidden">
              <div className="panel-header">
                <span className="panel-header-title">Request Detail</span>
              </div>
              <div className="panel-body flex min-h-0 flex-1 items-center justify-center">
                <p className="text-[12px] text-stone-600 font-mono text-center">
                  No requests in the queue.
                  <br />
                  Incoming broker emails and uploads will appear here.
                </p>
              </div>
            </div>
            )
          ) : (
          <div
            id="intake-request-detail"
            role="region"
            tabIndex={-1}
            aria-label={activeTab === 'emailed' ? 'Ingested Communication' : 'Request Detail'}
            className="panel flex min-h-0 flex-col overflow-hidden outline-none focus-visible:ring-1 focus-visible:ring-amber-300/40"
          >
            <div className="panel-header flex-shrink-0">
              <span className="panel-header-title">
                {activeTab === 'emailed' ? 'Ingested Communication' : 'Request Detail'}
              </span>
              <span className="text-[10px] font-mono text-stone-600">{selected.id}</span>
            </div>
            <div
              className={`panel-body min-h-0 flex-1 scrollbar-thin ${
                activeTab === 'emailed'
                  ? 'flex flex-col overflow-hidden'
                  : 'overflow-y-auto space-y-4'
              }`}
            >
              {activeTab === 'emailed' ? (
                <>
                  <IngestedCommunicationPanel
                    key={selected.id}
                    ingestionId={selected.id}
                    from={selected.from}
                    subject={selected.subject}
                    receivedAt={selected.receivedAt}
                    broker={selected.broker}
                    brokerFirm={selected.brokerFirm}
                    emailBody={demo ? buildDemoEmailBody(selected) : null}
                    attachments={selected.attachments}
                    emailEnvelope={selected.emailEnvelope}
                    rawFilePaths={selected.rawFilePaths}
                    activeSha={communicationSha}
                    onOpenSource={(sha) => openStoredSource(sha)}
                    onOpenAttachment={(att) => openAttachment(att)}
                  />
                </>
              ) : (
                <>
              {/* Email header */}
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-[11px]">
                  <Mail size={11} className="text-stone-600" />
                  <span className="text-stone-500">From:</span>
                  <span className="text-stone-200">{selected.from}</span>
                </div>
                <div className="flex items-center gap-2 text-[11px]">
                  <Send size={11} className="text-stone-600" />
                  <span className="text-stone-500">To:</span>
                  <span className="font-mono text-stone-200">
                    {selected.emailEnvelope?.to?.filter(Boolean).join(', ') || ORIGINATION_MAILBOX}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-[11px]">
                  <MessageSquare size={11} className="text-stone-600" />
                  <span className="text-stone-500">Subject:</span>
                  <span className="text-stone-200">{selected.subject}</span>
                </div>
                <div className="flex items-center gap-2 text-[11px]">
                  <Clock size={11} className="text-stone-600" />
                  <span className="text-stone-500">Received:</span>
                  <span className="text-stone-200">{selected.receivedAt}</span>
                </div>
                <div className="flex items-center gap-2 text-[11px]">
                  <User size={11} className="text-stone-600" />
                  <span className="text-stone-500">Broker:</span>
                  <span className="text-amber-300 font-medium">{selected.broker}</span>
                  <span className="text-stone-500">@ {selected.brokerFirm}</span>
                </div>
              </div>

              {!demo && (
                <BrokerResolutionCard
                  key={selected.id}
                  ingestionId={selected.id}
                  resolution={selected.parseReport?.brokerResolution}
                  brokerId={selected.brokerId}
                  brokerFirmId={selected.brokerFirmId}
                  onResolved={async () => {
                    await Promise.all([
                      queryClient.invalidateQueries({ queryKey: ['ingestion-queue'] }),
                      queryClient.invalidateQueries({ queryKey: ['ingestion-record', selected.id] }),
                      queryClient.invalidateQueries({ queryKey: ['broker-firms'] }),
                      queryClient.invalidateQueries({ queryKey: ['broker-contacts'] }),
                    ]);
                  }}
                />
              )}

              {/* Source files — original email + attachments open in the viewer */}
              {(selected.rawFilePaths?.length ?? 0) > 0 && (
                <>
                  <div className="gold-divider" />
                  <div>
                    <div className="flex items-center gap-1.5 mb-2">
                      <Paperclip size={11} className="text-stone-500" />
                      <span className="text-[10px] font-mono tracking-widest uppercase text-stone-500">
                        Source Files — click to review
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-1.5">
                      {selected.rawFilePaths!.map((f) => (
                        <button
                          key={f.sha256}
                          onClick={() => openStoredSource(f.sha256)}
                          onMouseEnter={() => setHoverSha(f.sha256)}
                          onMouseLeave={() => setHoverSha((cur) => (cur === f.sha256 ? null : cur))}
                          onFocus={() => setHoverSha(f.sha256)}
                          className={`flex items-center gap-2 rounded-lg border px-2 py-1.5 text-left transition-colors ${
                            hoverSha === f.sha256
                              ? 'border-amber-400/50 bg-amber-400/[0.08]'
                              : 'border-amber-400/10 bg-black/20 hover:border-amber-400/40'
                          }`}
                          title="Hover to preview · click to open the source"
                        >
                          {f.kind === 'email_source' ? <Mail size={11} className="text-blue-400" /> :
                           /\.(xlsx|xlsm|csv)$/i.test(f.filename) ? <FileSpreadsheet size={11} className="text-emerald-400" /> :
                           /\.pdf$/i.test(f.filename) ? <FileText size={11} className="text-red-400" /> :
                           <FileText size={11} className="text-blue-400" />}
                          <span className="text-[11px] text-stone-300 flex-1">
                            {f.kind === 'email_source' ? `Original email — ${f.filename}` : f.filename}
                          </span>
                          <Eye size={11} className="text-stone-600" />
                        </button>
                      ))}
                    </div>
                    {/* What each ZIP in the package gave up — and what it did not. */}
                    {!demo && (
                      <div className="mt-2">
                        <ArchiveOutcomeList archives={selected.parseReport?.archives} />
                      </div>
                    )}
                    {hoverSha && (
                      <div
                        className="mt-2"
                        onMouseEnter={() => setHoverSha(hoverSha)}
                        onMouseLeave={() => setHoverSha(null)}
                      >
                        <SourcePreviewPane
                          ingestionId={selected.id}
                          files={selected.rawFilePaths!}
                          activeSha={hoverSha}
                          envelope={selected.emailEnvelope}
                          compact
                          onOpenFull={(sha) => { setHoverSha(null); openStoredSource(sha); }}
                        />
                      </div>
                    )}
                  </div>
                </>
              )}
              {/* Attachments — click opens the source viewer when a binary is stored */}
              {!(selected.rawFilePaths?.length ?? 0) && selected.attachments && selected.attachments.length > 0 && (
                <>
                  <div className="gold-divider" />
                  <div>
                    <div className="flex items-center gap-1.5 mb-2">
                      <Paperclip size={11} className="text-stone-500" />
                      <span className="text-[10px] font-mono tracking-widest uppercase text-stone-500">
                        Attachments — click to review
                      </span>
                    </div>
                    <div className="space-y-1.5">
                      {selected.attachments.map((att, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => openAttachment(att)}
                          className="flex w-full items-center gap-2 rounded-lg border border-amber-400/10 bg-black/20 px-2 py-1.5 text-left transition-colors hover:border-amber-400/40"
                          title="Open attachment in the source viewer"
                        >
                          {att.type === 'excel' ? <FileSpreadsheet size={11} className="text-emerald-400" /> :
                           att.type === 'pdf' ? <FileText size={11} className="text-red-400" /> :
                           <FileText size={11} className="text-blue-400" />}
                          <span className="text-[11px] text-stone-300 flex-1">{att.filename}</span>
                          <span className="text-[10px] text-stone-600 font-mono">{att.sizeKb}KB</span>
                          {att.parsed && (
                            <span className="text-[9px] text-emerald-400 flex items-center gap-0.5">
                              <CheckCircle2 size={8} /> parsed
                            </span>
                          )}
                          <Eye size={11} className="text-stone-600" />
                        </button>
                      ))}
                      {demo && (
                        <p className="text-[10px] text-stone-600 pt-1">
                          Demo fixtures have no stored originals — the viewer opens with that notice until you upload a real email.
                        </p>
                      )}
                    </div>
                  </div>
                </>
              )}

              {/* Required fields, what was found for each, and where from.
                  This replaces three sections: Source Traceability (every
                  mapped field, in parser vocabulary, with no notion of which
                  ones matter), Extracted Data (a fixed nine values whose
                  membership had nothing to do with the required set) and AI
                  Assessment. */}
              <div className="gold-divider" />
              {disposition.kind === 'revision_pending' || disposition.kind === 'revision_on_killed_deal' ? (
                /* A revision is read for what it changes; the deal it revises
                   holds the required fields. Scoring a two-line reply as a
                   package reported "6 of 17 still needed" (LPR-24). */
                <p className="text-[11px] text-stone-400" data-testid="revision-required-fields-note">
                  {disposition.kind === 'revision_pending' && disposition.lenderDraft
                    ? 'Lender draft terms for deal'
                    : 'Revision to deal'}{' '}
                  <span className="font-mono text-amber-300">{disposition.loanId}</span> —
                  required fields are tracked on the deal file; this email is read only for the values
                  it changes (see the Email Thread panel).
                </p>
              ) : (
              <RequiredFieldsPanel
                coverage={requiredCoverage.data}
                loading={requiredCoverage.isLoading}
                sourceFiles={selected.rawFilePaths ?? []}
                onHoverSource={setHoverSha}
                onOpenSource={(source, cellRef) =>
                  openSource(source, cellRef ? { snippet: cellRef } : null)
                }
              />
              )}

              {/* The sponsor narrative the parser split off the sponsor line —
                  prose about the party, kept apart from the party's name. */}
              {!demo && selected.extracted.sponsorNarrative ? (
                <div className="rounded border border-stone-800 bg-stone-900/50 px-3 py-2">
                  <div className="flex items-center gap-1.5">
                    <User size={10} className="text-stone-500" />
                    <span className="text-[9px] font-mono uppercase tracking-widest text-stone-500">
                      Sponsor Narrative
                    </span>
                  </div>
                  <p className="mt-1 whitespace-pre-wrap text-[11px] leading-relaxed text-stone-300">
                    {selected.extracted.sponsorNarrative}
                  </p>
                </div>
              ) : null}

              {/* Why this record is not a deal yet, and what releases it. A
                  record that quietly fails to promote looks like every other
                  one in the queue, and the reviewer it waits on never learns. */}
              {!demo && selected.parseReport?.promotionHold && !selected.createdDealId && (
                <div className="flex items-start gap-2 rounded border border-amber-400/25 bg-amber-400/[0.05] px-2.5 py-2">
                  <AlertTriangle size={12} className="mt-px flex-shrink-0 text-amber-300" />
                  <div className="min-w-0">
                    <div className="text-[11px] font-medium text-amber-200">
                      Not a deal yet — {disposition.kind === 'revision_pending' && disposition.lenderDraft
                        ? "the lender's own draft terms wait on an existing deal"
                        : selected.parseReport.promotionHold.reason}
                    </div>
                    <div className="text-[10px] text-stone-400">
                      {disposition.kind === 'revision_pending' && disposition.lenderDraft
                        ? `these are the firm's own proposed terms for deal ${disposition.loanId}, not a revision from the broker; ${disposition.count} value${disposition.count === 1 ? ' waits' : 's wait'} for a person`
                        : selected.parseReport.promotionHold.detail}
                    </div>
                    <div className="mt-0.5 text-[10px] text-stone-300">
                      {selected.parseReport.promotionHold.clearsWhen}
                    </div>
                    {selected.parseReport.promotionHold.loanId && (
                      <button
                        type="button"
                        onClick={() => setDealFileId(selected.parseReport!.promotionHold!.loanId!)}
                        className="mt-1 inline-flex items-center gap-1 text-[10px] text-amber-300 underline decoration-dotted hover:text-amber-200"
                      >
                        <FolderOpen size={10} />
                        Open deal {selected.parseReport.promotionHold.loanId}
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* The shipped default: an emailed record waits for a person's
                  confirmation before it becomes a deal (Chris Young, 2 Nov
                  2026). Said so, so a waiting record never reads as stuck. */}
              {!demo && !selected.createdDealId && !selected.parseReport?.promotionHold
                && !selected.holdState && selected.status !== 'declined' && (
                <div
                  data-testid="awaiting-confirmation"
                  className="flex items-start gap-2 rounded border border-stone-700 bg-stone-900/60 px-2.5 py-2 text-[10px] text-stone-300"
                >
                  <Info size={11} className="mt-px shrink-0 text-stone-400" />
                  <span>
                    Not a deal yet: emailed deals wait for a person&apos;s confirmation. Review it, then press
                    Create Deal to add it to the Pipeline.
                  </span>
                </div>
              )}

              {/* A record held before hold reasons were saved has none. Said
                  so, rather than drawn as nothing — and never guessed at. */}
              {!demo && !selected.createdDealId && !selected.parseReport?.promotionHold
                && selected.holdState?.state === 'reason_not_recorded' && (
                <HoldReasonNotRecorded
                  key={selected.id}
                  ingestionId={selected.id}
                  detail={selected.holdState.detail}
                  hasReingest={Boolean(selected.parseReport?.reingest)}
                  onOpenDeal={(loanId) => setDealFileId(loanId)}
                />
              )}

              {/* A killed deal this request resembles. It is a new deal
                  regardless (stress test CMP-01); reopening the old one is a
                  choice the desk makes knowingly. */}
              {!demo && (selected.parseReport?.closedDealMatches ?? []).map((m) => (
                <div
                  key={m.loanId}
                  className="flex items-start gap-2 rounded border border-stone-700 bg-stone-900/60 px-2.5 py-2 text-[10px] text-stone-300"
                >
                  <Info size={11} className="mt-px shrink-0 text-stone-400" />
                  <span>{m.note}</span>
                </div>
              ))}

              {/* Deterministic engine evidence (live records) */}
              {selected.parseReport && (
                <GuidancePanel guidance={selected.parseReport.guidanceFields ?? []} />
              )}
              {selected.screenResults && <ScreenResultsPanel screens={selected.screenResults} />}
              <div className="flex items-center gap-2 rounded-lg border border-amber-400/15 bg-amber-400/[0.04] px-3 py-2 text-[10px]">
                <Ban size={10} className="shrink-0 text-amber-400" />
                <span className="text-stone-500">
                  Outbound communication requires human approval — no automated reply will be sent.
                </span>
              </div>
              {selected.parseReport?.thread && (
                <div id="intake-thread-panel">
                <ThreadPanel
                  key={selected.id}
                  ingestionId={selected.id}
                  thread={selected.parseReport.thread}
                  pendingRevisions={selected.parseReport.pendingRevisions ?? []}
                  createdDealId={selected.createdDealId ?? null}
                  onOpenDeal={demo ? undefined : (loanId) => setDealFileId(loanId)}
                  onApplied={() => {
                    queryClient.invalidateQueries({ queryKey: ['ingestion-queue'] });
                    queryClient.invalidateQueries({ queryKey: ['ingestion-record', selected.id] });
                    queryClient.invalidateQueries({ queryKey: ['intake-reconciliation'] });
                    queryClient.invalidateQueries({ queryKey: ['loans'] });
                    queryClient.invalidateQueries({ queryKey: ['pipeline-review-gaps'] });
                  }}
                />
                </div>
              )}
              {/* The engine now reads this package differently. A proposal, never
                  an application — a re-parse replaces values a person may have
                  chosen deliberately, so each is answered on its own in the
                  Data Intelligence Review. */}
              <ReparseNotice
                proposals={selected.parseReport?.reparse?.proposals ?? []}
                disabled={demo}
                onOpen={(field) => {
                  setDataReviewStart(`reparse:${selected.id}:${field}`);
                  setDataReviewOpen(true);
                }}
              />

              {selected.parseReport?.reingest && (
                <ReingestPanel
                  key={`reingest-${selected.id}`}
                  ingestionId={selected.id}
                  reingest={selected.parseReport.reingest}
                  ownDealId={selected.createdDealId}
                  onApplied={() => queryClient.invalidateQueries({ queryKey: ['ingestion-queue'] })}
                />
              )}

                </>
              )}

              <div className="gold-divider shrink-0" />

              {/* Actions */}
              <div className={`flex items-center gap-2 pt-1 ${activeTab === 'emailed' ? 'shrink-0' : ''}`}>
                {selected.status === 'converted' ? (
                  <div className="flex w-full flex-wrap items-center gap-2">
                    <div className="flex items-center gap-2 text-emerald-400 text-[12px] font-medium">
                      <CheckCircle2 size={14} />
                      {disposition.kind === 'filed_on_deal'
                        ? `${disposition.how === 'revision' ? 'Revision filed on' : 'Reconciled into'} deal ${selected.createdDealId}`
                        : `Deal created as ${selected.createdDealId}`}
                    </div>
                    {/* A promoted record is exactly where the desk works, so the
                        tools that chase what is missing stay reachable here
                        (stress test CMP-09) — not only before promotion. */}
                    <button
                      onClick={() => setInfoRequestId(selected.id)}
                      disabled={demo}
                      title={demo ? 'Turn demo data off to compose from a real record' : 'Compose an internal missing-fields request — never sent to the broker'}
                      className="btn-secondary flex items-center gap-1.5 text-[11px] px-3 py-1.5 disabled:opacity-50"
                    >
                      <Send size={11} />
                      Request More Info
                    </button>
                    <button
                      onClick={() => setRerunId(selected.id)}
                      disabled={demo}
                      title="Re-run the stored originals through the current parser and see what changed — the record is not modified"
                      className="btn-secondary flex items-center gap-1.5 text-[11px] px-3 py-1.5 disabled:opacity-50"
                    >
                      <RefreshCw size={11} />
                      Re-run parser
                    </button>
                    {/* The record already knew the deal id; until now there was
                        no way to reach it from here. Opens in place rather than
                        navigating away, so the intake context is not lost. */}
                    {selected.createdDealId && !demo && (
                      <button
                        onClick={() => setDealFileId(selected.createdDealId!)}
                        className="ml-auto flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium rounded-xl border border-amber-400/40 text-amber-300 hover:bg-amber-400/10 transition-colors"
                      >
                        <FolderOpen size={12} />
                        Open deal file
                      </button>
                    )}
                    {/* Ingested values stay pending until a person confirms
                        them; this is the one screen to do that on. */}
                    {selected.createdDealId && !demo && (
                      <button
                        onClick={() => setConfirmDealId(selected.createdDealId!)}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium rounded-xl border border-amber-400/40 text-amber-300 hover:bg-amber-400/10 transition-colors"
                      >
                        <ClipboardCheck size={12} />
                        Review &amp; confirm
                      </button>
                    )}
                  </div>
                ) : disposition.kind === 'revision_pending' ? (
                  <div className="flex w-full flex-wrap items-center gap-2">
                    {/* A reply revising a live deal is not a new request: no
                        Create Deal here (stress test LPR-24 / CRE-10). */}
                    <div className="flex items-center gap-2 text-amber-300 text-[12px] font-medium">
                      <GitBranch size={14} />
                      {/* The lender's own draft is the desk's proposal, not the
                          broker revising the deal (nd-039). */}
                      {disposition.lenderDraft ? 'Lender draft terms for deal' : 'Revision to deal'}{' '}
                      {disposition.loanId} — {disposition.count} value
                      {disposition.count === 1 ? '' : 's'} waiting in the Email Thread panel
                    </div>
                    {!demo && (
                      <button
                        onClick={() => setDealFileId(disposition.loanId)}
                        className="ml-auto flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium rounded-xl border border-amber-400/40 text-amber-300 hover:bg-amber-400/10 transition-colors"
                      >
                        <FolderOpen size={12} />
                        Open deal file
                      </button>
                    )}
                    {/* Two emails can describe two properties at one address
                        (Juno Square / Keep Safe Storage): the person who reads
                        them may decide this one is its own deal (nd-008). */}
                    {!demo && !disposition.lenderDraft && (
                      <button
                        onClick={() => doConvert(true)}
                        disabled={actioning}
                        data-testid="revision-separate-deal"
                        className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium rounded-xl border border-stone-700 text-stone-400 hover:text-amber-300 hover:border-amber-400/40 transition-colors disabled:opacity-50"
                      >
                        <Building2 size={12} />
                        This is a separate deal — create it
                      </button>
                    )}
                  </div>
                ) : selected.status === 'declined' ? (
                  <div className="flex items-center gap-2 w-full">
                    <div className="flex items-center gap-2 text-red-400 text-[12px] font-medium">
                      <XCircle size={14} />
                      Request declined
                    </div>
                    <button
                      onClick={archiveSelected}
                      disabled={actioning || demo}
                      className="ml-auto px-3 py-1.5 text-[11px] font-medium rounded-xl border border-stone-700 text-stone-500 hover:text-amber-300 hover:border-amber-400/40 transition-colors disabled:opacity-50"
                    >
                      Archive
                    </button>
                  </div>
                ) : (
                  <>
                    <button
                      onClick={() => convertSelected()}
                      disabled={actioning || demo}
                      title={demo ? 'Turn demo data off to convert real ingested deals' : undefined}
                      className="btn-primary flex items-center gap-1.5 text-[11px] px-4 py-1.5 disabled:opacity-50"
                    >
                      <ArrowRight size={12} />
                      {actioning ? 'Creating…' : 'Create Deal'}
                    </button>
                    <button
                      onClick={() => setInfoRequestId(selected.id)}
                      disabled={demo}
                      title={demo ? 'Turn demo data off to compose from a real record' : 'Compose an internal missing-fields request — never sent to the broker'}
                      className="btn-secondary flex items-center gap-1.5 text-[11px] px-3 py-1.5 disabled:opacity-50"
                    >
                      <Send size={11} />
                      Request More Info
                    </button>
                    <button
                      onClick={() => setRerunId(selected.id)}
                      disabled={demo}
                      title="Re-run the stored originals through the current parser and see what changed — the record is not modified"
                      className="btn-secondary flex items-center gap-1.5 text-[11px] px-3 py-1.5 disabled:opacity-50"
                    >
                      <RefreshCw size={11} />
                      Re-run parser
                    </button>
                    <button
                      onClick={declineSelected}
                      disabled={actioning || demo}
                      className="px-3 py-1.5 text-[11px] font-medium rounded-xl border border-stone-700 text-stone-500 hover:text-stone-300 hover:border-stone-500 transition-colors disabled:opacity-50"
                    >
                      Decline
                    </button>
                    <button
                      onClick={archiveSelected}
                      disabled={actioning || demo}
                      title="Remove from the queue (recoverable by ID) and free its content hash for re-upload"
                      className="ml-auto px-3 py-1.5 text-[11px] font-medium rounded-xl border border-stone-700 text-stone-500 hover:text-amber-300 hover:border-amber-400/40 transition-colors disabled:opacity-50"
                    >
                      Archive
                    </button>
                  </>
                )}
              </div>

              {actionError && (
                <div className="flex items-start gap-2 px-3 py-2 rounded-lg border border-red-500/30 bg-red-500/10 text-[11px] text-red-300">
                  <AlertTriangle size={13} className="mt-0.5 shrink-0" />
                  <span>{actionError}</span>
                </div>
              )}
              {threadBlocked && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => doConvert(true)}
                    disabled={actioning}
                    className="btn-secondary text-[10px] px-3 py-1.5 disabled:opacity-50"
                  >
                    This is a separate deal — create a new loan anyway
                  </button>
                  <span className="text-[9px] text-stone-600">
                    Otherwise, apply this message's updates to the existing loan in the Email Thread panel.
                  </span>
                </div>
              )}
            </div>
          </div>
          )}
        </div>
      )}

      {viewerSha && selected && viewerFiles.length > 0 && (
        <SourceViewerDialog
          ingestionId={selected.id}
          files={viewerFiles}
          initialSha={viewerSha}
          anchor={viewerAnchor}
          unavailableReason={viewerUnavailableReason}
          onClose={closeSourceViewer}
        />
      )}

      {rerunId && <RerunDialog ingestionId={rerunId} onClose={() => setRerunId(null)} />}
      {infoRequestId && (
        <InfoRequestDialog ingestionId={infoRequestId} onClose={() => setInfoRequestId(null)} />
      )}
      {/* Before the deal file, so a deal opened from a match candidate sits on
          top of the dialog it was opened from (same layer, later in the DOM). */}
      {matchPreview && selected && (
        <MatchPreviewDialog
          candidates={matchPreview.candidates}
          note={matchPreview.note}
          busy={actioning}
          onAttach={attachToExisting}
          onCreateNew={() => doConvert(true)}
          onClose={() => setMatchPreview(null)}
          onOpenDeal={(loanId) => setDealFileId(loanId)}
        />
      )}
      {confirmNew && selected && (
        <ConfirmNewDealDialog
          confirmation={confirmNew}
          busy={actioning}
          error={confirmError}
          onConfirm={(name, overrides) => void confirmNewDeal(name, overrides)}
          onClose={() => setConfirmNew(null)}
          onOpenDeal={(loanId) => setDealFileId(loanId)}
        />
      )}
      {confirmDealId && (
        <ConfirmationViewDialog loanId={confirmDealId} onClose={() => setConfirmDealId(null)} />
      )}
      {dealFileId && (
        <DealFileDialog
          loanId={dealFileId}
          fallbackTitle={selected?.extracted?.property}
          onClose={() => setDealFileId(null)}
        />
      )}

      {dataReviewOpen && selected && (
        <DataReviewPanel
          ingestionId={selected.id}
          initialItemId={dataReviewStart ?? undefined}
          onClose={() => {
            setDataReviewOpen(false);
            setDataReviewStart(null);
          }}
        />
      )}

    </div>
  );
}
